// react
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject, ContextUsersPermissions } from '../../pages/Project/Project.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';
import { ContextBanner, ContextUser } from '../../../App.context';

// services
import { uploadAiDocument } from '../../../services/aiApi';
import {
	AccountEarmarkSummary,
	fetchActivity,
	getAccountEarmarks,
	getActivityEarmarks,
} from '../../../services/api';

// v2 components
import {
	DialogDocumentPicker,
	type PickedDocument,
} from '../../dialogs/DialogDocumentPicker/DialogDocumentPicker';

// mui
import {
	Alert,
	alpha,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	InputAdornment,
	MenuItem,
	Paper,
	SelectChangeEvent,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AttachFile as IconAttachFile,
	CheckCircle as IconCheckCircle,
	Close as IconClose,
	CloudUpload as IconCloudUpload,
	Description as IconContract,
	ExpandLess as IconExpandLess,
	ExpandMore as IconExpandMore,
	GppGood as IconGate,
	Info as IconInfo,
	Assignment as IconWorkPlan,
	Paid as IconPaid,
	Person as IconPerson,
	Save as IconSave,
	Send as IconSend,
	Warning as IconWarning,
	Work as IconActivity,
	Group as IconGroup,
} from '@mui/icons-material';

// types
import {
	Account,
	DocumentType,
	ExchangeRateByDate,
	ExpenseType,
	Loan,
	Condition,
	V2Contract,
	DataRequestField,
	DataRequestResponse,
	SectionResponse,
	Voucher,
	VoucherChannel,
	VoucherPayload,
} from '../../../@types';

// utils
import { formatCurrency } from '../../../utils/currency';
import {
	getContractCommitmentAmount,
	getContractSupplierName,
} from '../../pages/ProjectV2/Contracts/Contracts.utils';
import { resolveContractFxRate } from '../../../utils/fxRate';
import { isAnswerUnexpected, evaluateExpectation } from '../../../utils/expectationEvaluator';

// config
import { featureFlags } from '../../../config/featureFlags';

// exchange rates
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { useExchangeRates } from '../../../hooks/useExchangeRates';

// financing sources (reused from the v1 transaction form)
import { Amounts } from '../../fields/Amounts/Amounts';
import { Select } from '../../fields/Select/Select';
import { ListItemTextAccount } from '../../ui/ListItemTextAccount/ListItemTextAccount';
import { BlockchainProcessing } from '../../ui/BlockchainProcessing/BlockchainProcessing';
import { CardInvoiceDetails } from '../../cards/CardInvoiceDetails/CardInvoiceDetails';

// Props

export interface DialogPrepareForPaymentProps {
	contract: V2Contract | null;
	isOpen: boolean;
	linkedConditionId?: number | null;
	onClose: () => void;
	onSuccess?: () => void;
	/**
	 * Render variant:
	 * - 'dialog' (default): renders inside an MUI Dialog overlay.
	 * - 'page': renders inline as a full-width embedded page (no overlay).
	 *   Used by the contract detail page to show prepare-payment as an
	 *   embedded view rather than a modal.
	 */
	variant?: 'dialog' | 'page';
	/**
	 * When provided, document chips in the per-condition listing become
	 * clickable and invoke this callback. The host page is responsible for
	 * opening a viewer for the document (e.g. DialogDocumentViewer).
	 */
	onDocClick?: (documentId: string, documentName: string) => void;
}

// Internal Types

interface ConditionSelection {
	condition: Condition;
	isBase: boolean;
	isChecked: boolean;
	isPaid: boolean;
	isTemplate?: boolean;
}

interface UploadedDocument {
	id: string;
	name: string;
	type: string;
	size: number;
}

// Component

export const DialogPrepareForPayment = (props: DialogPrepareForPaymentProps) => {
	const {
		contract,
		isOpen,
		linkedConditionId,
		onClose,
		onSuccess,
		variant = 'dialog',
		onDocClick,
	} = props;
	const { t } = useTranslation();
	const theme = useTheme();

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { setBanner } = useContext(ContextBanner);
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);
	const {
		activities,
		conditions: allConditions,
		createVoucher,
		getInvoicesByContract,
		refetchConditions,
		refetchVouchers,
		updateCondition,
		updateVoucher,
		vouchers,
		workPlans,
	} = useContext(ContextSpendPlan);

	// Stepper state
	const [isSubmitting, setIsSubmitting] = useState(false);
	// Per-condition draft save state — only one card spinner active at a time
	const [savingDraftConditionId, setSavingDraftConditionId] = useState<number | null>(null);

	// Expanded conditions state (for showing data entry fields)
	const [conditionUploadedDocs, setConditionUploadedDocs] = useState<
		Record<number, Record<string, UploadedDocument[]>>
	>({});
	// Tracks which document field is currently uploading: { [conditionId]: fieldName | null }
	const [uploadingField, setUploadingField] = useState<Record<number, string | null>>({});
	// Upload error feedback: { [conditionId]: { [fieldId]: errorMessage } }
	const [uploadErrors, setUploadErrors] = useState<Record<number, Record<string, string>>>({});

	// Reveal the inline validation summary only after the user tries to submit,
	// so the Create Voucher button can stay clickable while the form is incomplete.
	const [submitAttempted, setSubmitAttempted] = useState(false);

	// Data request field responses (for collecting specific information)
	// Structure: { [conditionId]: { [fieldId]: string (value) } }
	const [dataRequestResponses, setDataRequestResponses] = useState<
		Record<number, Record<string, string>>
	>({});

	// Payment method selection
	const [paymentMethod, setPaymentMethod] = useState<'DA_REIMBURSEMENT' | 'DIRECT_PAYMENT'>(
		'DA_REIMBURSEMENT'
	);
	const [paymentChannel, setPaymentChannel] = useState<string>('BANK');

	// Direct payment classification fields (Story #43 — only used when no invoices provide these)
	const [directComponents, setDirectComponents] = useState<{ id: number; name: string }[]>([]);
	const [directCategories, setDirectCategories] = useState<{ id: number; name: string }[]>([]);
	const [directExpenseTypes, setDirectExpenseTypes] = useState<ExpenseType[]>([]);
	const [directAccounts, setDirectAccounts] = useState<Account[]>([]);
	// Activity's responsible entity, fetched once the contract's activity is
	// known. Used to scope the direct-from-account picker to accounts the
	// responsible entity owns or co-owns — anything else the user shouldn't
	// be able to pay from for this activity.
	const [responsibleEntityId, setResponsibleEntityId] = useState<number | null>(null);
	const [selectedComponentId, setSelectedComponentId] = useState<number | null>(null);
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
	const [selectedExpenseTypeId, setSelectedExpenseTypeId] = useState<number | null>(null);
	const [selectedFromAccountId, setSelectedFromAccountId] = useState<number | null>(null);
	const [voucherComments, setVoucherComments] = useState('');

	// Account-derived fields — populated when From Account is selected
	const [selectedAccountData, setSelectedAccountData] = useState<Account | null>(null);
	const [designatedAccounts, setDesignatedAccounts] = useState<Account[]>([]);
	const [availableLoans, setAvailableLoans] = useState<Loan[]>([]);
	const [selectedDesignatedAccountId, setSelectedDesignatedAccountId] = useState<number | null>(
		null
	);
	// Financing-source attribution. Reuses the v1 transaction form's
	// `<Amounts>` widget verbatim — one entry per loan on the source account,
	// each with an amount and per-source tax withheld in the source-account
	// currency. State shape mirrors FormAddTransaction.values.amounts so the
	// widget's onChange handler is identical: `{ loan, amount, tax }[]`.
	const [financingSourceAmounts, setFinancingSourceAmounts] = useState<
		{ loan: number; amount: number; tax: number }[]
	>([]);

	// Exchange rates — currency list is purely derived from the selected
	// source-account currency, the contract's currencies, and the project's
	// currencies. User-typed rate/date values are stored in a map keyed by
	// `${base}|${target}` so they survive re-derivation and support multiple
	// source accounts (one base per unique source currency). Story #88.
	const [exchangeRateValues, setExchangeRateValues] = useState<
		Record<string, { exchangeRate: number; dateExchangeRate: string }>
	>({});
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// ── Earmark picker state (Story #80) ────────────────────────────────────
	const [earmarkAllocations, setEarmarkAllocations] = useState<
		{
			spendPlanId: number;
			spendPlanName: string;
			spendPlanStatus: string;
			accountId: number;
			amountPlanned: number;
			amountVouchered: number;
			remaining: number;
		}[]
	>([]);
	const [selectedEarmarkPlanIds, setSelectedEarmarkPlanIds] = useState<number[]>([]);
	const [earmarkAmounts, setEarmarkAmounts] = useState<Record<number, number>>({});
	const [earmarksLoaded, setEarmarksLoaded] = useState(false);

	// Funding source. A payment can be funded either from a spend-plan earmark
	// (the default whenever the activity has approved earmarks) or directly
	// from an account that holds available, non-earmarked balance — no spend
	// plan required. The direct path lets teams pay out of an account before
	// converting/earmarking funds, gated only by the account's balance.
	const [fundingSource, setFundingSource] = useState<'spendPlan' | 'account'>('spendPlan');
	// Per-account earmark summaries (balance / available), keyed by account id.
	// Used to gate a direct payment on the account's non-earmarked balance.
	const [accountEarmarkSummaries, setAccountEarmarkSummaries] = useState<
		Record<number, AccountEarmarkSummary>
	>({});

	// Collapsed/expanded state for condition cards
	const [expandedConditionIds, setExpandedConditionIds] = useState<Set<number>>(new Set());

	// Derived data from contract. `contractCurrency` is the contract's own
	// primary currency so percentage-of-contract maths is evaluated in the
	// same currency the figures are denominated in. `contractAmount` is
	// `null` when the contract carries no amount in that currency (a
	// degenerate state — the dialog is meaningless without one); each
	// consumer below handles the null case explicitly rather than silently
	// coercing it into a different currency or a fabricated zero.
	const supplierName = contract ? getContractSupplierName(contract) : '';
	const contractCurrency = contract?.amounts?.[0]?.currency || 'USD';
	const contractAmount: number | null = contract
		? getContractCommitmentAmount(contract, { currency: contractCurrency })
		: null;

	// Project local currency and the contract's implied FX rate. The canonical
	// resolver picks the contract's own basis (commitment scalars, then the
	// multi-currency amount rows) and only falls back to the work plan when the
	// contract has nothing of its own. Drafts rarely carry captured commitments
	// but often still encode a rate via, e.g., a USD amount alongside an ETB
	// amount.
	const projectLocalCurrency = project?.currencies?.find((c) => c.type === 'LOCAL')?.currency;
	const contractWorkPlan = contract
		? workPlans.find((wp) => wp.id === contract.workPlan)
		: undefined;
	const contractFx = resolveContractFxRate(contract, contractWorkPlan, project);
	const payoutLocalEquivalent = (reportingValue: number): string | undefined => {
		if (!contractFx || !projectLocalCurrency) return undefined;
		return formatCurrency({
			currency: projectLocalCurrency,
			value: reportingValue * contractFx.rate,
		});
	};

	// Earmark currency lookup — earmark amounts are denominated in the source
	// account's currency, NOT the contract currency. Display them accordingly so
	// EUR earmarks aren't mislabeled as USD (Story #88, Group A).
	const getEarmarkCurrency = useCallback(
		(accountId: number): string => {
			const account = directAccounts.find((a) => a.id === accountId);
			return account?.currency || contractCurrency;
		},
		[directAccounts, contractCurrency]
	);

	// Spend-plan → currency lookup. Each earmark/spend plan is tied to a single
	// source account whose currency is the natural denomination of the voucher
	// being paid out of that earmark. Multi-earmark payments can therefore mix
	// currencies across vouchers in the same submission. Falls back to contract
	// currency when the spend plan / account can't be resolved (Story #88).
	const getSpendPlanCurrency = useCallback(
		(spendPlanId: number | null | undefined): string => {
			if (!spendPlanId) {
				// Direct (no-earmark) payment: the voucher is denominated in the
				// selected source account's currency, mirroring the earmark path so
				// the account-currency balance deducts correctly. Falls back to the
				// contract currency before an account is chosen.
				const acct = directAccounts.find((a) => a.id === selectedFromAccountId);
				return acct?.currency || contractCurrency;
			}
			const alloc = earmarkAllocations.find((e) => e.spendPlanId === spendPlanId);
			if (!alloc) return contractCurrency;
			return getEarmarkCurrency(alloc.accountId);
		},
		[
			earmarkAllocations,
			getEarmarkCurrency,
			contractCurrency,
			directAccounts,
			selectedFromAccountId,
		]
	);

	// Get vouchers for this contract to track paid conditions
	const contractVouchers = useMemo(() => {
		if (!contract) return [];
		return vouchers.filter((v) => {
			const vContract = typeof v.contract === 'number' ? v.contract : v.contract?.id;
			return vContract === contract.id;
		});
	}, [contract, vouchers]);

	// Get set of condition IDs that already have vouchers for THIS contract
	// Base conditions can be paid once per contract (shared work plan gates)
	const paidConditionIds = useMemo(() => {
		const ids = new Set<number>();
		contractVouchers.forEach((v) => {
			// GENERATED vouchers are working drafts — they must remain visible so the
			// user can continue editing. Only vouchers that have advanced past draft
			// (VERIFIED / READY / REDEEMED) actually consume the condition slot.
			if (v.status === 'CANCELLED' || v.status === 'GENERATED') return;
			const conditionId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
			if (conditionId) ids.add(conditionId);
		});
		return ids;
	}, [contractVouchers]);

	// Filter base conditions from work plan (gates/prerequisites)
	const baseConditions = useMemo(() => {
		if (!contract) return [];
		return allConditions
			.filter((c) => {
				const condWorkPlan = typeof c.workPlan === 'number' ? c.workPlan : c.workPlan?.id;
				// Base conditions: belong to the work plan and have conditionType 'base'
				return condWorkPlan === contract.workPlan && c.conditionType === 'base';
			})
			.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
	}, [allConditions, contract]);

	// Filter payment conditions for this contract (payment milestones)
	// When linkedConditionId is provided (invoice flow), only show that specific milestone
	const paymentConditions = useMemo(() => {
		if (!contract) return [];
		return allConditions
			.filter((c) => {
				// Match conditions linked to this contract
				const contractMatch =
					typeof c.contract === 'number'
						? c.contract === contract.id
						: typeof c.contract === 'object' && c.contract !== null
							? c.contract.id === contract.id
							: false;
				if (!contractMatch) return false;
				// When opened from invoice page, only show the linked milestone and its templates
				if (linkedConditionId) {
					return c.id === linkedConditionId || c.sourceCondition != null;
				}
				return true;
			})
			.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
	}, [allConditions, contract, linkedConditionId]);

	// Resolve the contract's activity classification. This is the canonical
	// component / category / expense-type for any voucher emitted from this
	// contract — every voucher carries it so downstream transactions and
	// reports can group by financial classification without inspecting the
	// originating invoice (which is optional, may be missing line items, and
	// historically could disagree with the activity).
	const activityClassification = useMemo(() => {
		if (!contract) return { componentId: null, categoryId: null, expenseTypeId: null };
		const activity = activities.find((a) => a.id === contract.planningActivity);
		if (!activity) return { componentId: null, categoryId: null, expenseTypeId: null };
		const componentId =
			typeof activity.component === 'number'
				? activity.component
				: (activity.component?.id ?? null);
		const categoryId =
			typeof activity.category === 'number' ? activity.category : (activity.category?.id ?? null);
		const expenseTypeId =
			typeof activity.expenseType === 'number'
				? activity.expenseType
				: (activity.expenseType?.id ?? null);
		return { componentId, categoryId, expenseTypeId };
	}, [contract, activities]);

	// Union the activity's classification with anything contributed by invoice
	// line items. Used only for surfacing template-driven additional
	// requirements (the activity's templates always apply; line items can
	// pull in additional templates but never replace the activity's).
	const invoiceEntityIds = useMemo(() => {
		const componentIds = new Set<number>();
		const categoryIds = new Set<number>();
		const expenseTypeIds = new Set<number>();
		if (activityClassification.componentId) componentIds.add(activityClassification.componentId);
		if (activityClassification.categoryId) categoryIds.add(activityClassification.categoryId);
		if (activityClassification.expenseTypeId)
			expenseTypeIds.add(activityClassification.expenseTypeId);
		if (contract) {
			const contractInvoices = getInvoicesByContract(contract.id);
			contractInvoices.forEach((inv) => {
				(inv.lineItems || []).forEach((li) => {
					if (li.componentId) componentIds.add(li.componentId);
					if (li.categoryId) categoryIds.add(li.categoryId);
					if (li.expenseTypeId) expenseTypeIds.add(li.expenseTypeId);
				});
			});
		}
		return {
			componentIds: Array.from(componentIds),
			categoryIds: Array.from(categoryIds),
			expenseTypeIds: Array.from(expenseTypeIds),
		};
	}, [contract, getInvoicesByContract, activityClassification]);

	// Direct payment flag — true when the contract has no activity
	// classification and no invoice line items supply one. In that case the
	// dialog must surface the manual classification picker so the user can
	// supply component/category/expense-type for the voucher.
	const isDirectPayment = useMemo(() => {
		const { componentIds, categoryIds, expenseTypeIds } = invoiceEntityIds;
		return componentIds.length === 0 && categoryIds.length === 0 && expenseTypeIds.length === 0;
	}, [invoiceEntityIds]);

	// Fetch classification dropdowns for direct payment path
	useEffect(() => {
		if (!isOpen || !project?.id || !isDirectPayment) return;
		const baseUrl = process.env.REACT_APP_API_URL;
		Promise.all([
			fetch(
				`${baseUrl}/components?project=${project.id}&status=ACTIVE&orderBy=name&order=ASC`
			).then((r) => r.json()),
			fetch(
				`${baseUrl}/categories?project=${project.id}&status=ACTIVE&orderBy=name&order=ASC`
			).then((r) => r.json()),
			fetch(
				`${baseUrl}/expense-types?project=${project.id}&populate=documentTypes&status=ACTIVE&orderBy=name&order=ASC`
			).then((r) => r.json()),
		])
			.then(([compRes, catRes, etRes]) => {
				if (compRes.data) setDirectComponents(compRes.data);
				if (catRes.data) setDirectCategories(catRes.data);
				if (etRes.data) setDirectExpenseTypes(etRes.data);
			})
			.catch(() => {
				// Non-critical — selectors will be empty
			});
	}, [isOpen, project?.id, isDirectPayment]);

	// Fetch accounts (always, not gated by isDirectPayment).
	// When the activity's responsible entity is known we scope the picker to
	// accounts that entity owns or is associated with so the user can't
	// accidentally route a payment through another entity's account.
	useEffect(() => {
		if (!isOpen || !project?.id) return;
		const baseUrl = process.env.REACT_APP_API_URL;
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		const params = new URLSearchParams({
			project: String(project.id),
			fields: 'currency,id,institution,name,status,type',
			orderBy: 'name',
			order: 'ASC',
		});
		if (responsibleEntityId != null) {
			params.set('entity', String(responsibleEntityId));
		}
		fetch(`${baseUrl}/accounts?${params.toString()}`, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		})
			.then((r) => r.json())
			.then((accRes) => {
				if (accRes.data) setDirectAccounts(accRes.data);
			})
			.catch(() => {
				// Non-critical — account selector will be empty
			});
	}, [isOpen, project?.id, responsibleEntityId]);

	// Fetch account details when From Account is selected (designated accounts, loans, currency)
	useEffect(() => {
		if (!selectedFromAccountId) {
			setSelectedAccountData(null);
			setDesignatedAccounts([]);
			setAvailableLoans([]);
			setSelectedDesignatedAccountId(null);
			setFinancingSourceAmounts([]);
			return;
		}
		const baseUrl = process.env.REACT_APP_API_URL;
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		fetch(`${baseUrl}/accounts/${selectedFromAccountId}?populate=accounts,loans`, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		})
			.then((r) => r.json())
			.then((res) => {
				// The accounts list endpoint always returns `data` as an array,
				// even for single-id lookups (matches v1 FormAddTransaction).
				const acct: Account | null = Array.isArray(res?.data)
					? res.data[0] || null
					: res?.data || null;
				setSelectedAccountData(acct);
				// Designated accounts = sub-accounts with type DESIGNATED
				const da =
					(acct?.accounts as Account[] | undefined)?.filter((a) => a.type === 'DESIGNATED') || [];
				setDesignatedAccounts(da);
				// Available loans from the account
				setAvailableLoans((acct?.loans as Loan[] | undefined) || []);
				// Reset dependent selections
				setSelectedDesignatedAccountId(null);
				setFinancingSourceAmounts([]);
			})
			.catch(() => {
				setSelectedAccountData(null);
				setDesignatedAccounts([]);
				setAvailableLoans([]);
			});
	}, [selectedFromAccountId]);

	// ── Selected source accounts (multi-account aware) ─────────────────
	// When spend-plan earmarks are selected, derive the source accounts from
	// the earmark allocations (one account per spend plan, may be multiple
	// distinct accounts). When no earmarks are selected, fall back to the
	// single dropdown selection. Story #88: support paying from multiple
	// accounts in one prepare-payment flow.
	const selectedFromAccountIds = useMemo(() => {
		if (selectedEarmarkPlanIds.length > 0) {
			const ids = selectedEarmarkPlanIds
				.map((spId) => earmarkAllocations.find((a) => a.spendPlanId === spId)?.accountId)
				.filter((id): id is number => typeof id === 'number');
			return Array.from(new Set(ids));
		}
		return selectedFromAccountId ? [selectedFromAccountId] : [];
	}, [selectedEarmarkPlanIds, earmarkAllocations, selectedFromAccountId]);

	const selectedFromAccounts = useMemo(() => {
		return selectedFromAccountIds
			.map((id) => directAccounts.find((a) => a.id === id))
			.filter((a): a is Account => !!a);
	}, [selectedFromAccountIds, directAccounts]);

	// Keep the legacy single-account state (`selectedFromAccountId`) in sync
	// with the earmark-derived selection so that:
	//   • unchecking the last earmark clears the auto-selected account (and,
	//     in turn, hides the FX section), and
	//   • toggling between earmarks updates the account fetched by the
	//     `/accounts/:id` detail effect.
	// Only runs when earmarks exist for this contract — otherwise the user's
	// manual dropdown choice is preserved.
	useEffect(() => {
		// Direct-from-account funding keeps the manual dropdown choice; only the
		// spend-plan path derives the account from earmark selection.
		if (fundingSource !== 'spendPlan') return;
		if (earmarkAllocations.length === 0) return;
		// When earmarks exist for this contract, the source account is purely
		// derived from the earmark selection — never the manual dropdown
		// fallback. If nothing is checked, force `selectedFromAccountId` to
		// null so the FX section and detail fetch reset.
		const desired = selectedEarmarkPlanIds.length > 0 ? (selectedFromAccountIds[0] ?? null) : null;
		if (desired !== selectedFromAccountId) {
			setSelectedFromAccountId(desired);
		}
	}, [
		fundingSource,
		earmarkAllocations.length,
		selectedEarmarkPlanIds.length,
		selectedFromAccountIds,
		selectedFromAccountId,
	]);

	// Build the FX-rate section. The base is the SOURCE ACCOUNT's currency so
	// each rate row reads "1 <reporting> = <rate> <account>" — i.e. the
	// reporting currency sits on the left, identical to Fundschain 1.0. With
	// the account currency as the base, the financing-source conversions
	// (account amount ÷ rate) yield the reporting figure exactly as v1 does.
	// Targets = every other distinct currency in scope (reporting / contract
	// amounts, project currencies, designated account). Falls back to the
	// contract/reporting currency only before a source account is resolved.
	const fxBaseCurrency = useMemo(() => {
		const firstSource = selectedFromAccounts.find((a) => !!a.currency)?.currency;
		if (firstSource) return firstSource;
		if (contractCurrency) return contractCurrency;
		return project?.currencies?.find((c) => c.type === 'REPORTING')?.currency || null;
	}, [project?.currencies, contractCurrency, selectedFromAccounts]);

	const fxTargetCurrencies = useMemo(() => {
		if (!fxBaseCurrency) return [] as string[];
		// FX is only required when at least one source account is selected
		// AND its currency differs from the base — otherwise no payment-time
		// conversion is needed.
		const sourceCurrencies = selectedFromAccounts
			.map((a) => a.currency)
			.filter((c): c is string => !!c);
		if (sourceCurrencies.length === 0) return [];
		const extras: string[] = [...sourceCurrencies];
		if (contractCurrency) extras.push(contractCurrency);
		if (contract?.amounts?.length) {
			for (const a of contract.amounts) {
				if (a.currency) extras.push(a.currency);
			}
		}
		if (project?.currencies?.length) {
			for (const c of project.currencies) {
				if (c.currency) extras.push(c.currency);
			}
		}
		if (designatedAccounts.length > 0 && designatedAccounts[0].currency) {
			extras.push(designatedAccounts[0].currency);
		}
		return getCurrenciesForExchangeRates({
			currency: fxBaseCurrency,
			currenciesExtra: extras,
		}).filter(Boolean);
	}, [
		fxBaseCurrency,
		selectedFromAccounts,
		contractCurrency,
		contract?.amounts,
		project?.currencies,
		designatedAccounts,
		getCurrenciesForExchangeRates,
	]);

	// Materialise rate rows, pulling user-typed values from the per-pair
	// `exchangeRateValues` map (keyed `${base}|${target}` for forward-compat).
	const exchangeRates: ExchangeRateByDate[] = useMemo(() => {
		if (!fxBaseCurrency) return [];
		return fxTargetCurrencies.map((target) => {
			const key = `${fxBaseCurrency}|${target}`;
			return {
				currency: target,
				exchangeRate: exchangeRateValues[key]?.exchangeRate ?? 0,
				dateExchangeRate: exchangeRateValues[key]?.dateExchangeRate ?? '',
			};
		});
	}, [fxBaseCurrency, fxTargetCurrencies, exchangeRateValues]);

	// ── Fetch the activity's responsible entity ─────────────────────────
	// Used to scope `/accounts` lookups so the direct-from-account picker
	// only offers accounts whose owner / associate is the entity responsible
	// for this contract's activity.
	useEffect(() => {
		if (!isOpen || !contract?.planningActivity) {
			setResponsibleEntityId(null);
			return;
		}
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		if (!accessToken || !idToken) return;
		let cancelled = false;
		fetchActivity({ accessToken, idToken }, contract.planningActivity)
			.then((res) => {
				if (cancelled || res.error || !res.data) return;
				const entity = res.data.entity;
				const entityId =
					typeof entity === 'number'
						? entity
						: entity && typeof entity === 'object'
							? entity.id
							: null;
				setResponsibleEntityId(entityId ?? null);
			})
			.catch(() => {
				/* non-critical — picker falls back to the project-wide list */
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, contract?.planningActivity]);

	// ── Fetch earmark allocations for this contract's activity ──────────
	useEffect(() => {
		if (!isOpen || !contract?.planningActivity) {
			setEarmarkAllocations([]);
			setSelectedEarmarkPlanIds([]);
			setEarmarkAmounts({});
			setEarmarksLoaded(false);
			return;
		}

		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		if (!accessToken || !idToken) return;

		getActivityEarmarks(
			{ accessToken, idToken },
			{ activity: contract.planningActivity, contract: contract.id }
		)
			.then((res) => {
				const data = res.data || [];
				setEarmarkAllocations(data);
				setEarmarksLoaded(true);
				// Default to the spend-plan path when earmarks exist; otherwise the
				// user can only fund the payment directly from an account.
				setFundingSource(data.length > 0 ? 'spendPlan' : 'account');
				if (data.length === 1) {
					// Single earmark — auto-select plan and account
					setSelectedEarmarkPlanIds([data[0].spendPlanId]);
					setSelectedFromAccountId(data[0].accountId);
				} else if (data.length > 1) {
					// Multiple earmarks — still pre-select account if all share the same one
					const uniqueAccounts = [...new Set(data.map((d) => d.accountId))];
					if (uniqueAccounts.length === 1) {
						setSelectedFromAccountId(uniqueAccounts[0]);
					}
				}
			})
			.catch(() => {
				setEarmarkAllocations([]);
				setEarmarksLoaded(true);
				setFundingSource('account');
			});
	}, [isOpen, contract?.planningActivity, contract?.id]);

	// ── Fetch per-account earmark summaries (balance / available) ───────
	// Powers the direct-payment guardrail: a payment funded straight from an
	// account is allowed only while its available (non-earmarked) balance
	// covers the payout. Accounts without spend plans are absent here and fall
	// back to their full balance in the validation below.
	useEffect(() => {
		if (!isOpen || !project?.id) return;
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		if (!accessToken || !idToken) return;
		let cancelled = false;
		getAccountEarmarks({ accessToken, idToken }, { project: project.id })
			.then((res) => {
				if (cancelled || res.error || !res.data) return;
				const map: Record<number, AccountEarmarkSummary> = {};
				res.data.forEach((s) => {
					map[s.accountId] = s;
				});
				setAccountEarmarkSummaries(map);
			})
			.catch(() => {
				/* non-critical — direct funding falls back to the account balance */
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, project?.id]);

	// Conditions state
	const [conditionSelections, setConditionSelections] = useState<ConditionSelection[]>([]);
	const [notes, setNotes] = useState<string>('');
	// Per-split tax: keyed by `${conditionId}|${spendPlanId}` so each
	// (condition, earmark) pair carries its own tax in its own account currency.
	// Multi-earmark payments naturally produce one tax per voucher per currency;
	// single-earmark payments use the same map with the single selected
	// spendPlanId. Story #88 (Option A — per-split tax).
	const [taxWithheldBySplit, setTaxWithheldBySplit] = useState<Record<string, string>>({});
	const [taxModeByCondition, setTaxModeByCondition] = useState<
		Record<number, 'amount' | 'percent'>
	>({});

	// Helper for per-split tax keys. spendPlanId may be null/undefined when no
	// earmark is selected — fall back to a sentinel so the user's input survives
	// even before they pick an earmark.
	const getTaxKey = useCallback(
		(conditionId: number, spendPlanId: number | null | undefined): string =>
			`${conditionId}|${spendPlanId ?? 'none'}`,
		[]
	);

	// Field-level override state: { [conditionId]: { [fieldId]: { overridden: boolean, comment: string } } }
	const [fieldOverrides, setFieldOverrides] = useState<
		Record<number, Record<string, { overridden: boolean; comment: string }>>
	>({});

	// Inline exception notes — per field, collected when user answers differently from expectedResponse
	// Structure: { [conditionId]: { [fieldId]: string (explanation) } }
	const [exceptionNotes, setExceptionNotes] = useState<Record<number, Record<string, string>>>({});

	// Skip-with-reason dialog shared by two document requirements:
	//  - per-condition document fields (the reason is stored on fieldOverrides so
	//    it flows to the voucher payload's overrideComment), and
	//  - expense-type "Required Documents" (the reason is stored on
	//    requiredDocSkips below).
	// Both surface the same traceability-impact warning to the reviewer.
	const [skipDialog, setSkipDialog] = useState<
		| { kind: 'field'; conditionId: number; fieldId: string; label: string }
		| { kind: 'requiredDoc'; docTypeId: number; label: string }
		| null
	>(null);
	const [skipReason, setSkipReason] = useState('');

	// Expense-type required documents that were skipped with a reason
	// ({ [docTypeId]: reason }). Skipping keeps the voucher creatable while
	// flagging the traceability impact in the UI.
	const [requiredDocSkips, setRequiredDocSkips] = useState<Record<number, string>>({});

	// Initialize condition selections when conditions change
	// Filter out already-paid conditions — they should not appear in the dialog at all
	useEffect(() => {
		// Map base conditions - these are from the work plan (exclude paid)
		const baseSelections: ConditionSelection[] = baseConditions
			.filter((condition) => !paidConditionIds.has(condition.id))
			.map((condition) => ({
				condition,
				isBase: true,
				isChecked: condition.status === 'VERIFIED' || condition.status === 'APPROVED',
				isPaid: false,
			}));

		// Map payment conditions - these are contract-specific (exclude paid)
		const paymentSelections: ConditionSelection[] = paymentConditions
			.filter((condition) => !paidConditionIds.has(condition.id))
			.map((condition) => ({
				condition,
				isBase: false,
				// Legacy cloned-template conditions are always required (auto-checked).
				// New flow uses inline template-sourced sections; these branches remain
				// to gracefully render any pre-existing clone rows still in the DB.

				// When linkedConditionId is set, auto-check that specific milestone
				isChecked:
					!!condition.sourceCondition ||
					(linkedConditionId != null && condition.id === linkedConditionId) ||
					condition.status === 'VERIFIED' ||
					condition.status === 'APPROVED',
				isPaid: false,
				// Legacy clones (sourceCondition set) are locked & required
				isTemplate: !!condition.sourceCondition,
			}));

		setConditionSelections([...baseSelections, ...paymentSelections]);
	}, [baseConditions, paymentConditions, paidConditionIds, linkedConditionId]);

	// Required document types (from expense type configuration in Settings)
	const [requiredDocTypes, setRequiredDocTypes] = useState<DocumentType[]>([]);
	const [requiredDocUploads, setRequiredDocUploads] = useState<
		Record<number, UploadedDocument | null>
	>({});
	const [requiredDocUploadingId, setRequiredDocUploadingId] = useState<number | null>(null);

	// Fetch required document types based on expense types (invoice path OR direct selection)
	useEffect(() => {
		if (!isOpen || !project?.id) return;
		const { expenseTypeIds } = invoiceEntityIds;

		// For direct payment path, use the manually selected expense type
		const effectiveExpenseTypeIds =
			expenseTypeIds.length > 0
				? expenseTypeIds
				: selectedExpenseTypeId
					? [selectedExpenseTypeId]
					: [];

		if (effectiveExpenseTypeIds.length === 0) {
			setRequiredDocTypes([]);
			return;
		}

		// For direct path, expense types are already fetched with documentTypes populated
		if (isDirectPayment && selectedExpenseTypeId) {
			const selected = directExpenseTypes.find((et) => et.id === selectedExpenseTypeId);
			if (selected) {
				const requiredDocs = (selected.documentTypes as DocumentType[]).filter(
					(dt) => dt.isRequired
				);
				setRequiredDocTypes(requiredDocs);
			}
			return;
		}

		const fetchRequiredDocTypes = async () => {
			try {
				const res = await fetch(
					`${process.env.REACT_APP_API_URL}/expense-types?project=${project.id}&populate=documentTypes&status=ACTIVE`
				);
				const data = await res.json();
				if (data.data) {
					// Filter to only expense types used in this contract's invoices
					const matchingExpenseTypes = (data.data as ExpenseType[]).filter((et) =>
						effectiveExpenseTypeIds.includes(et.id)
					);
					// Collect all required document types (deduplicated by ID)
					const requiredDocs = new Map<number, DocumentType>();
					matchingExpenseTypes.forEach((et) => {
						(et.documentTypes as DocumentType[]).forEach((dt) => {
							if (dt.isRequired && !requiredDocs.has(dt.id)) {
								requiredDocs.set(dt.id, dt);
							}
						});
					});
					setRequiredDocTypes(Array.from(requiredDocs.values()));
				}
			} catch {
				// Non-critical — enforcement won't block if fetch fails
			}
		};
		fetchRequiredDocTypes();
	}, [
		isOpen,
		project?.id,
		invoiceEntityIds,
		selectedExpenseTypeId,
		isDirectPayment,
		directExpenseTypes,
	]);

	// Reset form when dialog opens with a new contract
	useEffect(() => {
		if (isOpen && contract) {
			setIsSubmitting(false);
			setNotes('');
			setFieldOverrides({});
			setDataRequestResponses({});
			setExceptionNotes({});
			setUploadErrors({});
			setSubmitAttempted(false);
			setRequiredDocUploads({});
			setRequiredDocSkips({});
			setSelectedComponentId(null);
			setSelectedCategoryId(null);
			setSelectedExpenseTypeId(null);
			setSelectedFromAccountId(null);
			setVoucherComments('');
			setSelectedAccountData(null);
			setDesignatedAccounts([]);
			setAvailableLoans([]);
			setSelectedDesignatedAccountId(null);
			setFinancingSourceAmounts([]);
			setExchangeRateValues({});
		}
	}, [isOpen, contract]);

	// Existing supplier-owned vouchers (one per condition while in flight).
	// EXT supplier writes land in a per-condition voucher with status
	// GENERATED carrying the partial sectionResponses. The PIU's Prepare-
	// for-Payment dialog needs to hydrate from these so the supplier's
	// work shows up instead of being silently overwritten by a fresh
	// voucher on submit. Indexed by conditionId for O(1) lookup at both
	// hydration time and submit time.
	const existingVouchersByCondition = useMemo(() => {
		const map: Record<number, Voucher> = {};
		if (!contract) return map;
		(vouchers || [])
			.filter((v) => {
				const vContractId = typeof v.contract === 'number' ? v.contract : v.contract?.id;
				const vConditionId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
				return (
					vContractId === contract.id &&
					vConditionId != null &&
					(v.status || '').toUpperCase() === 'GENERATED'
				);
			})
			.forEach((v) => {
				const vConditionId = (typeof v.condition === 'number' ? v.condition : v.condition?.id)!;
				// If multiple GENERATED vouchers exist for the same condition
				// (shouldn't happen but defend), keep the most-recent one.
				const existing = map[vConditionId];
				if (!existing || new Date(v.created) > new Date(existing.created)) {
					map[vConditionId] = v;
				}
			});
		return map;
	}, [contract, vouchers]);

	// Hydrate dataRequestResponses + conditionUploadedDocs from any
	// GENERATED supplier-owned vouchers when the dialog opens.
	useEffect(() => {
		if (!isOpen || !contract) return;
		const seedResponses: Record<number, Record<string, string>> = {};
		const seedDocs: Record<number, Record<string, UploadedDocument[]>> = {};
		Object.entries(existingVouchersByCondition).forEach(([condIdStr, voucher]) => {
			const condId = parseInt(condIdStr, 10);
			(voucher.sectionResponses || []).forEach((section) => {
				(section.fieldResponses || []).forEach((field) => {
					if (field.value != null && field.value !== '') {
						seedResponses[condId] = seedResponses[condId] || {};
						seedResponses[condId][field.fieldId] = field.value;
					}
					const docIds =
						field.documentIds && field.documentIds.length
							? field.documentIds
							: field.documentId
								? [field.documentId]
								: [];
					const docNames =
						field.documentNames && field.documentNames.length
							? field.documentNames
							: field.documentName
								? [field.documentName]
								: [];
					if (docIds.length) {
						seedDocs[condId] = seedDocs[condId] || {};
						seedDocs[condId][field.fieldId] = docIds.map((id, i) => ({
							id,
							name: docNames[i] || '',
							type: '',
							size: 0,
						}));
					}
				});
			});
		});
		if (Object.keys(seedResponses).length) setDataRequestResponses(seedResponses);
		if (Object.keys(seedDocs).length) setConditionUploadedDocs(seedDocs);
	}, [isOpen, contract, existingVouchersByCondition]);

	// Calculated Values

	// Helper: get all requirement fields from requirements sections
	const getAllFields = (condition: Condition): DataRequestField[] => {
		return (condition.verificationSections || []).flatMap((s) => s.fields || []);
	};

	// Helper: check if a field's answer is "unexpected" (fails any expectation rule).
	// Uses the full rule engine from expectationEvaluator.ts.
	// For yesno fields with no expectation configured, defaults to expecting 'yes'.
	const isUnexpectedAnswer = (field: DataRequestField, answer: string): boolean => {
		if (!answer) return false;
		const defaultExpected = field.fieldType === 'yesno' ? 'yes' : undefined;
		return isAnswerUnexpected(field.expectedResponse, answer, field.fieldType, defaultExpected);
	};

	// Helper: get a human-readable failure reason for the exception UI
	const getExpectationFailureReason = (field: DataRequestField, answer: string): string => {
		const result = evaluateExpectation(field.expectedResponse, answer, field.fieldType);
		return result.failureReason || 'Response does not meet the expected criteria';
	};

	// Helper: check if an inline exception note is required and satisfied for a field
	const isExceptionNoteSatisfied = useCallback(
		(conditionId: number, field: DataRequestField, answer: string): boolean => {
			if (!isUnexpectedAnswer(field, answer)) return true;
			const note = (exceptionNotes[conditionId] || {})[field.id] || '';
			return note.trim().length >= 10;
		},
		[exceptionNotes]
	);

	// Check if all inline exception notes are satisfied (min 10 chars where required)
	// Skip conditions that are already VERIFIED/APPROVED — their verification is complete
	const allExceptionNotesSatisfied = useMemo(() => {
		return conditionSelections
			.filter(
				(cs) =>
					cs.isChecked &&
					!cs.isPaid &&
					cs.condition.status !== 'VERIFIED' &&
					cs.condition.status !== 'APPROVED'
			)
			.every((cs) => {
				const fields = getAllFields(cs.condition).filter(
					(f) => f.fieldType === 'yesno' || f.expectedResponse != null
				);
				const responses = dataRequestResponses[cs.condition.id] || {};
				return fields.every((f) => isExceptionNoteSatisfied(cs.condition.id, f, responses[f.id]));
			});
	}, [conditionSelections, dataRequestResponses, isExceptionNoteSatisfied]);

	// Helper: check if a required field is satisfied (filled OR properly overridden)
	const isFieldSatisfied = useCallback(
		(
			conditionId: number,
			field: DataRequestField,
			responses: Record<string, string>,
			uploadedDocs: Record<string, UploadedDocument[]>
		): boolean => {
			// Check if field is filled
			if (field.fieldType === 'document') {
				if ((uploadedDocs[field.id] || []).length > 0) return true;
			} else if (field.fieldType === 'yesno') {
				if (responses[field.id] === 'yes' || responses[field.id] === 'no') return true;
			} else {
				if (responses[field.id]?.trim().length > 0) return true;
			}
			// A reason was supplied (override / skip). Document requirements can
			// always be skipped with a reason — the traceability impact is surfaced
			// in the UI — while other field types keep the existing allowOverride
			// gate and 10-character minimum.
			const override = fieldOverrides[conditionId]?.[field.id];
			if (override?.overridden) {
				const reason = override.comment.trim();
				if (field.fieldType === 'document' && reason.length > 0) return true;
				if (field.allowOverride && reason.length >= 10) return true;
			}
			return false;
		},
		[fieldOverrides]
	);

	// Skip-with-reason handlers. openSkipDialog / undoSkip target per-condition
	// document fields; openRequiredDocSkipDialog / undoRequiredDocSkip target the
	// expense-type "Required Documents". confirmSkip commits whichever is open.
	const openSkipDialog = (conditionId: number, fieldId: string, label: string) => {
		setSkipReason('');
		setSkipDialog({ kind: 'field', conditionId, fieldId, label });
	};

	const openRequiredDocSkipDialog = (docTypeId: number, label: string) => {
		setSkipReason('');
		setSkipDialog({ kind: 'requiredDoc', docTypeId, label });
	};

	const confirmSkip = () => {
		if (!skipDialog || !skipReason.trim()) return;
		const reason = skipReason.trim();
		if (skipDialog.kind === 'field') {
			const { conditionId, fieldId } = skipDialog;
			setFieldOverrides((prev) => ({
				...prev,
				[conditionId]: {
					...(prev[conditionId] || {}),
					[fieldId]: { overridden: true, comment: reason },
				},
			}));
		} else {
			const { docTypeId } = skipDialog;
			setRequiredDocSkips((prev) => ({ ...prev, [docTypeId]: reason }));
		}
		setSkipDialog(null);
		setSkipReason('');
	};

	const undoSkip = (conditionId: number, fieldId: string) => {
		setFieldOverrides((prev) => {
			const condMap = { ...(prev[conditionId] || {}) };
			delete condMap[fieldId];
			return { ...prev, [conditionId]: condMap };
		});
	};

	const undoRequiredDocSkip = (docTypeId: number) => {
		setRequiredDocSkips((prev) => {
			const next = { ...prev };
			delete next[docTypeId];
			return next;
		});
	};

	// Check if all required data request fields have been filled (or overridden)
	// Skip conditions that are already VERIFIED/APPROVED — their data was collected during verification
	const allRequiredDataFieldsFilled = useMemo(() => {
		return conditionSelections
			.filter(
				(cs) =>
					cs.isChecked &&
					!cs.isPaid &&
					cs.condition.status !== 'VERIFIED' &&
					cs.condition.status !== 'APPROVED'
			)
			.every((cs) => {
				const fields = getAllFields(cs.condition);
				const requiredFields = fields.filter((f) => f.required);
				if (requiredFields.length === 0) return true;
				const responses = dataRequestResponses[cs.condition.id] || {};
				const uploadedDocs = conditionUploadedDocs[cs.condition.id] || {};
				return requiredFields.every((f) =>
					isFieldSatisfied(cs.condition.id, f, responses, uploadedDocs)
				);
			});
	}, [conditionSelections, dataRequestResponses, conditionUploadedDocs, isFieldSatisfied]);

	// Counts — exclude legacy template clones (they're additional requirements, not standalone milestones)
	const selectedCount = conditionSelections.filter((cs) => cs.isChecked && !cs.isTemplate).length;
	const totalCount = conditionSelections.filter((cs) => !cs.isTemplate).length;
	// How many actual milestones (non-base, non-template) are checked
	const milestoneSelectedCount = conditionSelections.filter(
		(cs) => cs.isChecked && !cs.isBase && !cs.isTemplate
	).length;

	// Total gross payout of selected milestones (for earmark sufficiency check)
	const selectedPayoutTotal = useMemo(() => {
		return conditionSelections
			.filter((cs) => cs.isChecked && !cs.isBase && !cs.isTemplate)
			.reduce((sum, cs) => {
				// Percentage milestones contribute nothing when contractAmount
				// is unknown (no commitment in the contract's own currency).
				const amt =
					cs.condition.paymentAmount ||
					(cs.condition.paymentPercentage && contractAmount != null
						? (contractAmount * cs.condition.paymentPercentage) / 100
						: 0);
				return sum + amt;
			}, 0);
	}, [conditionSelections, contractAmount]);

	// Financing-sources derived values. Entries with zero amount AND zero tax
	// are filtered out so a user typing only into the rows they care about
	// produces a clean payload. `financingSourcesLoanId` is the legacy
	// single-loan column the v1 mirror writer still reads; we populate it from
	// the first entered source so single-source vouchers round-trip into v1
	// without a mirror-writer change.
	const financingSourcesArray = useMemo(() => {
		return financingSourceAmounts
			.filter((s) => s.amount !== 0 || s.tax !== 0)
			.map((s) => ({ loanId: s.loan, amount: s.amount, tax: s.tax }));
	}, [financingSourceAmounts]);

	const financingSourcesTotal = useMemo(
		() => financingSourcesArray.reduce((sum, s) => sum + s.amount + s.tax, 0),
		[financingSourcesArray]
	);

	// When the selected source account has exactly one loan, there is no
	// allocation decision to make — the FS row is implicitly that loan. We
	// surface that loan id here so the legacy `voucher.loanId` column and the
	// JSONB `financing_sources` array can both auto-fill at submit time
	// without forcing the user to fiddle with a one-option widget.
	const soleLoanId = useMemo(
		() => (availableLoans.length === 1 ? availableLoans[0].id : null),
		[availableLoans]
	);

	const financingSourcesLoanId = useMemo(
		() =>
			financingSourcesArray.length > 0 ? financingSourcesArray[0].loanId : (soleLoanId ?? null),
		[financingSourcesArray, soleLoanId]
	);

	// Helper — converts an amount in `from` currency to `to` currency using
	// the user-entered FX rates (keyed by `${fxBaseCurrency}|${target}` where
	// "1 target = rate base"). Returns null when the conversion can't be
	// resolved yet (rate not entered). Identity returns the input.
	const convertViaFx = useCallback(
		(amount: number, from: string, to: string): number | null => {
			if (!from || !to || from === to) return amount;
			if (!fxBaseCurrency) return null;
			const toBase = (a: number, ccy: string): number | null => {
				if (ccy === fxBaseCurrency) return a;
				const r = exchangeRateValues[`${fxBaseCurrency}|${ccy}`]?.exchangeRate ?? 0;
				return r > 0 ? a * r : null;
			};
			const fromBase = (a: number, ccy: string): number | null => {
				if (ccy === fxBaseCurrency) return a;
				const r = exchangeRateValues[`${fxBaseCurrency}|${ccy}`]?.exchangeRate ?? 0;
				return r > 0 ? a / r : null;
			};
			const inBase = toBase(amount, from);
			if (inBase === null) return null;
			return fromBase(inBase, to);
		},
		[fxBaseCurrency, exchangeRateValues]
	);

	// Sum of selected earmark allocations expressed in the **contract currency**
	// using the user-entered FX rates. When any rate is missing, that earmark's
	// contribution is unresolved (returned as null) so the UI can hint the
	// user to enter the rate.
	//
	// Single-earmark mode: the earmark is a *funding bucket*, not an
	// allocation that must equal the payout. The user is saying "fund this
	// payout entirely from this one earmark", so the allocation is exactly
	// the payout total (already in contract currency). We only need FX so
	// the per-account remaining check below can compare apples to apples.
	// Multi-earmark mode: each per-earmark amount the user entered is
	// converted into contract currency and summed; the result must equal
	// the payout (reconcile check below).
	const earmarkAllocatedInContract = useMemo(() => {
		if (selectedEarmarkPlanIds.length === 0) return { total: 0, unresolved: false };
		if (selectedEarmarkPlanIds.length === 1) {
			return { total: selectedPayoutTotal, unresolved: false };
		}
		let total = 0;
		let unresolved = false;
		for (const spId of selectedEarmarkPlanIds) {
			const alloc = earmarkAllocations.find((a) => a.spendPlanId === spId);
			if (!alloc) continue;
			const acct = directAccounts.find((a) => a.id === alloc.accountId);
			const accountCurrency = acct?.currency || contractCurrency;
			const amount = earmarkAmounts[spId] ?? 0;
			const converted = convertViaFx(amount, accountCurrency, contractCurrency);
			if (converted === null) {
				unresolved = true;
			} else {
				total += converted;
			}
		}
		return { total, unresolved };
	}, [
		selectedEarmarkPlanIds,
		earmarkAllocations,
		directAccounts,
		earmarkAmounts,
		contractCurrency,
		convertViaFx,
		selectedPayoutTotal,
	]);

	// Validation

	// Notes are optional (exception explanations are now inline per field)
	const notesRequired = false;

	// Payment method fields validation
	const isPaymentFieldsValid = useMemo(() => {
		if (milestoneSelectedCount === 0) return true;
		return true; // Payment route is selected by default; bank details collected downstream
	}, [milestoneSelectedCount]);

	// Required documents validation — all required doc types must have an uploaded file
	const allRequiredDocsUploaded = useMemo(() => {
		if (requiredDocTypes.length === 0) return true;
		// A required document is satisfied when it is uploaded OR explicitly skipped
		// with a reason — the traceability impact is surfaced in the UI.
		return requiredDocTypes.every(
			(dt) => requiredDocUploads[dt.id] != null || !!requiredDocSkips[dt.id]?.trim()
		);
	}, [requiredDocTypes, requiredDocUploads, requiredDocSkips]);

	const hasEarmarks = earmarkAllocations.length > 0;

	// Earmark allocations must reconcile to the milestone payout total (in
	// the contract currency, after applying the user-entered FX rates).
	// Unresolved (missing rates) also blocks submit. Story #88.
	const earmarkAllocationsReconcile = useMemo(() => {
		if (selectedEarmarkPlanIds.length === 0) return true;
		if (earmarkAllocatedInContract.unresolved) return false;
		return Math.abs(earmarkAllocatedInContract.total - selectedPayoutTotal) <= 0.005;
	}, [selectedEarmarkPlanIds.length, earmarkAllocatedInContract, selectedPayoutTotal]);

	// Every FX rate that's rendered must be filled in (rate > 0 AND date
	// provided) before submit. Story #88.
	const allExchangeRatesProvided = useMemo(() => {
		if (exchangeRates.length === 0) return true;
		return exchangeRates.every((r) => r.exchangeRate > 0 && !!r.dateExchangeRate);
	}, [exchangeRates]);

	// ── Direct-from-account funding (no spend plan) ─────────────────────
	// The selected account's currency and its available (non-earmarked)
	// balance. Available comes from the per-account earmark summary when the
	// account has spend plans; otherwise the account carries no earmarks and
	// its full balance is available.
	const directAccountCurrency = useMemo(() => {
		if (selectedFromAccountId == null) return contractCurrency;
		const acct = directAccounts.find((a) => a.id === selectedFromAccountId);
		return acct?.currency || contractCurrency;
	}, [selectedFromAccountId, directAccounts, contractCurrency]);

	const directAccountAvailable = useMemo<number | null>(() => {
		if (selectedFromAccountId == null) return null;
		const summary = accountEarmarkSummaries[selectedFromAccountId];
		if (summary) return summary.available;
		if (selectedAccountData && selectedAccountData.id === selectedFromAccountId) {
			return selectedAccountData.balances.reduce((t, b) => t + (b.balance || 0), 0);
		}
		return null;
	}, [selectedFromAccountId, accountEarmarkSummaries, selectedAccountData]);

	// Payout converted into the source account's currency (null when an FX
	// rate it depends on hasn't been entered yet).
	const directPayoutInAccountCcy = useMemo<number | null>(
		() => convertViaFx(selectedPayoutTotal, contractCurrency, directAccountCurrency),
		[convertViaFx, selectedPayoutTotal, contractCurrency, directAccountCurrency]
	);

	const directFundsSufficient =
		directAccountAvailable != null &&
		directPayoutInAccountCcy != null &&
		directAccountAvailable + 0.005 >= directPayoutInAccountCcy;

	// Designated source account (legacy FundsChain "first-touch account"
	// concept). The voucher row already persists `designatedAccountId`; this
	// flag just makes the picker behave as required when the chosen From
	// Account exposes DESIGNATED sub-accounts. Accounts that don't expose any
	// keep silent — there is nothing to pick.
	const isDesignatedAccountValid =
		designatedAccounts.length === 0 || selectedDesignatedAccountId != null;

	// Funding is valid when either the spend-plan earmarks reconcile to the
	// payout, or (direct path) an account with enough available balance is
	// selected.
	const isFundingValid =
		fundingSource === 'spendPlan'
			? hasEarmarks && earmarkAllocationsReconcile
			: selectedFromAccountId != null && directFundsSufficient;

	// Financing-sources expected total — the gross payout restated into the
	// source-account currency, since the widget rows are denominated there.
	// Falls back to the contract-currency total when the FX conversion isn't
	// resolvable yet (the validation will pass through to FX validation
	// instead of double-flagging).
	const financingSourcesExpectedTotal = useMemo(() => {
		if (!fxBaseCurrency || !contractCurrency) return selectedPayoutTotal;
		const converted = convertViaFx(selectedPayoutTotal, contractCurrency, fxBaseCurrency);
		return converted ?? selectedPayoutTotal;
	}, [selectedPayoutTotal, fxBaseCurrency, contractCurrency, convertViaFx]);

	// Valid when no sources entered (legacy single-loan-via-loanId remains
	// null) OR when entered and summing within a 1¢ rounding margin of the
	// expected total. Multi-earmark and multi-currency edge cases skip the
	// match check — the widget remains usable for loan attribution but the
	// sum-must-match guard only applies when the dialog is single-currency.
	const isFinancingSourcesValid = useMemo(() => {
		if (financingSourcesArray.length === 0) return true;
		if (selectedEarmarkPlanIds.length > 1) return true;
		return Math.abs(financingSourcesTotal - financingSourcesExpectedTotal) < 0.01;
	}, [
		financingSourcesArray.length,
		financingSourcesTotal,
		financingSourcesExpectedTotal,
		selectedEarmarkPlanIds.length,
	]);

	const isFormValid =
		milestoneSelectedCount > 0 &&
		allRequiredDataFieldsFilled &&
		allExceptionNotesSatisfied &&
		isPaymentFieldsValid &&
		allRequiredDocsUploaded &&
		isFundingValid &&
		isDesignatedAccountValid &&
		allExchangeRatesProvided &&
		isFinancingSourcesValid;

	// Human-readable list of what still blocks submission. Surfaced inline once
	// the user clicks Create Voucher; the button itself stays enabled so they can
	// trigger this reveal instead of guessing why it was greyed out.
	const validationIssues: string[] = [];
	if (milestoneSelectedCount === 0)
		validationIssues.push(
			t('spendPlan.preparePayment.issue.milestone', 'Select at least one milestone to pay.')
		);
	if (!allRequiredDataFieldsFilled)
		validationIssues.push(
			t('spendPlan.preparePayment.issue.dataFields', 'Complete all required information fields.')
		);
	if (!allExceptionNotesSatisfied)
		validationIssues.push(
			t('spendPlan.preparePayment.issue.exceptionNotes', 'Add a note for each skipped requirement.')
		);
	if (!isPaymentFieldsValid)
		validationIssues.push(
			t(
				'spendPlan.preparePayment.issue.paymentFields',
				'Enter a valid payment amount for each selected milestone.'
			)
		);
	if (!allRequiredDocsUploaded)
		validationIssues.push(
			t(
				'spendPlan.preparePayment.issue.docs',
				'Upload every required document, or skip it with a reason.'
			)
		);
	if (!isFundingValid)
		validationIssues.push(
			t('spendPlan.preparePayment.issue.funding', 'Select a valid funding source.')
		);
	if (!isDesignatedAccountValid)
		validationIssues.push(
			t('spendPlan.preparePayment.issue.designatedAccount', 'Select a designated account.')
		);
	if (!allExchangeRatesProvided)
		validationIssues.push(
			t('spendPlan.preparePayment.issue.fx', 'Provide all required exchange rates.')
		);
	if (!isFinancingSourcesValid)
		validationIssues.push(
			t('spendPlan.preparePayment.issue.financing', 'Complete the financing source allocation.')
		);

	// Handlers

	// Handler for exchange rate field changes — writes into the per-pair
	// values map keyed by `${base}|${target}` so multiple source accounts
	// (multiple bases) can each have their own rates.
	const handleChangeExchangeRatesForBase = (
		base: string,
		target: string,
		name: string,
		value: string | number
	) => {
		const key = `${base}|${target}`;
		setExchangeRateValues((prev) => ({
			...prev,
			[key]: {
				exchangeRate: name === 'exchangeRate' ? Number(value) : (prev[key]?.exchangeRate ?? 0),
				dateExchangeRate:
					name === 'dateExchangeRate' ? String(value) : (prev[key]?.dateExchangeRate ?? ''),
			},
		}));
	};

	// Handler for data request field response changes
	const handleDataRequestResponse = (conditionId: number, fieldId: string, value: string) => {
		ensureConditionChecked(conditionId);
		setDataRequestResponses((prev) => ({
			...prev,
			[conditionId]: {
				...(prev[conditionId] || {}),
				[fieldId]: value,
			},
		}));
	};

	// Handler for inline exception note changes
	const handleExceptionNote = (conditionId: number, fieldId: string, note: string) => {
		ensureConditionChecked(conditionId);
		setExceptionNotes((prev) => ({
			...prev,
			[conditionId]: {
				...(prev[conditionId] || {}),
				[fieldId]: note,
			},
		}));
	};

	const handleConditionToggle = (conditionId: number) => {
		setConditionSelections((prev) =>
			prev.map((cs) => {
				// Don't allow toggling already-paid or template conditions
				// Already-paid milestones and legacy additional-requirement clones cannot be toggled.
				if (cs.isPaid || cs.isTemplate) return cs;
				return cs.condition.id === conditionId ? { ...cs, isChecked: !cs.isChecked } : cs;
			})
		);
	};

	// Auto-select a milestone the instant the user starts completing its
	// requirements, so entered data never silently goes unpaid because the
	// checkbox was left unticked. No-ops (returns the same array, so no
	// re-render) when the row is already checked, paid, or a template clone.
	const ensureConditionChecked = (conditionId: number) => {
		setConditionSelections((prev) => {
			const target = prev.find((cs) => cs.condition.id === conditionId);
			if (!target || target.isPaid || target.isTemplate || target.isChecked) return prev;
			return prev.map((cs) =>
				cs.condition.id === conditionId ? { ...cs, isChecked: true } : cs
			);
		});
	};

	// Condition-specific document upload handler — uses REAL uploadAiDocument API
	// Supports multi-doc based on field.maxDocuments (1 = replace, N/0 = append)

	// Helper: look up maxDocuments for a field
	const maxDocsForField = (conditionId: number, fieldId: string): number => {
		const condition = allConditions.find((c) => c.id === conditionId);
		if (!condition) return 1;
		for (const section of condition.verificationSections || []) {
			const field = section.fields?.find((f: DataRequestField) => f.id === fieldId);
			if (field?.maxDocuments !== undefined) return field.maxDocuments;
		}
		return 1; // default: single document
	};

	// Helper: look up documentTypeId for a field. Set on fields auto-seeded
	// from `expense_type_document_type`; propagated to the upload tag so the
	// voucher→V1 transaction bridge can attribute the file to the right
	// document_type_id and V1 traceability keeps working.
	const documentTypeIdForField = (conditionId: number, fieldId: string): number | undefined => {
		const condition = allConditions.find((c) => c.id === conditionId);
		if (!condition) return undefined;
		for (const section of condition.verificationSections || []) {
			const field = section.fields?.find((f: DataRequestField) => f.id === fieldId);
			if (field?.documentTypeId != null) return field.documentTypeId;
		}
		return undefined;
	};

	// Document picker state
	const [pickerOpen, setPickerOpen] = useState(false);
	const [pickerConditionId, setPickerConditionId] = useState<number>(0);
	const [pickerFieldId, setPickerFieldId] = useState<string>('');
	const [pickerMaxDocs, setPickerMaxDocs] = useState<number>(1);

	const openDocPicker = (
		conditionId: number,
		fieldId: string,
		maxDocs: number,
		currentDocs: UploadedDocument[]
	) => {
		setPickerConditionId(conditionId);
		setPickerFieldId(fieldId);
		setPickerMaxDocs(maxDocs);
		setPickerOpen(true);
	};

	const handlePickerConfirm = (picked: PickedDocument[]) => {
		const docs: UploadedDocument[] = picked.map((p) => ({
			id: p.id,
			name: p.filename,
			type: p.doc_type || '',
			size: 0,
		}));
		ensureConditionChecked(pickerConditionId);
		setConditionUploadedDocs((prev) => ({
			...prev,
			[pickerConditionId]: {
				...(prev[pickerConditionId] || {}),
				[pickerFieldId]: docs,
			},
		}));
	};

	const handleConditionDocUpload = async (
		conditionId: number,
		fieldName: string,
		e: ChangeEvent<HTMLInputElement>
	) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		// Validate prerequisites before attempting upload
		if (!user?.id) {
			console.error('Upload failed: user is not authenticated');
			setUploadErrors((prev) => ({
				...prev,
				[conditionId]: {
					...(prev[conditionId] || {}),
					[fieldName]: 'You must be logged in to upload documents.',
				},
			}));
			e.target.value = '';
			return;
		}
		if (!contract) {
			console.error('Upload failed: no contract context');
			setUploadErrors((prev) => ({
				...prev,
				[conditionId]: {
					...(prev[conditionId] || {}),
					[fieldName]: 'Contract context is missing.',
				},
			}));
			e.target.value = '';
			return;
		}

		// Only take the first file (single-document per requirement)
		const file = files[0];
		ensureConditionChecked(conditionId);
		setUploadingField((prev) => ({ ...prev, [conditionId]: fieldName }));
		// Clear any previous error for this field
		setUploadErrors((prev) => {
			const condErrors = { ...(prev[conditionId] || {}) };
			delete condErrors[fieldName];
			return { ...prev, [conditionId]: condErrors };
		});

		try {
			const docTypeId = documentTypeIdForField(conditionId, fieldName);
			const result = await uploadAiDocument({
				file,
				project_id: project?.id,
				source_type: 'condition',
				source_id: conditionId,
				tag: [
					`condition:${conditionId}`,
					contract ? `contract:${contract.id}` : '',
					fieldName,
					docTypeId != null ? `document_type:${docTypeId}` : '',
				]
					.filter(Boolean)
					.join(','),
			});

			const uploadedDoc: UploadedDocument = {
				id: result.document_id,
				name: file.name,
				type: file.type,
				size: file.size,
			};

			// Find the field to check maxDocuments
			const fieldMaxDocs = maxDocsForField(conditionId, fieldName);
			setConditionUploadedDocs((prev) => {
				const existing = (prev[conditionId] || {})[fieldName] || [];
				const updated = fieldMaxDocs === 1 ? [uploadedDoc] : [...existing, uploadedDoc];
				return {
					...prev,
					[conditionId]: {
						...(prev[conditionId] || {}),
						[fieldName]: updated,
					},
				};
			});
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Upload failed. Please try again.';
			console.error(`Upload failed for condition ${conditionId}, field ${fieldName}:`, err);
			setUploadErrors((prev) => ({
				...prev,
				[conditionId]: { ...(prev[conditionId] || {}), [fieldName]: message },
			}));
		} finally {
			setUploadingField((prev) => ({ ...prev, [conditionId]: null }));
			// Reset the file input so the same file can be re-selected
			e.target.value = '';
		}
	};

	// Remove a condition-specific uploaded document
	const handleRemoveConditionDoc = (conditionId: number, fieldName: string, docId: string) => {
		setConditionUploadedDocs((prev) => ({
			...prev,
			[conditionId]: {
				...(prev[conditionId] || {}),
				[fieldName]: ((prev[conditionId] || {})[fieldName] || []).filter((d) => d.id !== docId),
			},
		}));
	};

	// Required document type upload handler
	const handleRequiredDocUpload = async (docTypeId: number, e: ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;
		const file = files[0];
		setRequiredDocUploadingId(docTypeId);
		try {
			const result = await uploadAiDocument({
				file,
				project_id: project?.id,
				source_type: 'voucher',
				tag: contract ? `contract:${contract.id},document_type:${docTypeId}` : undefined,
			});
			setRequiredDocUploads((prev) => ({
				...prev,
				[docTypeId]: {
					id: result.document_id,
					name: file.name,
					type: file.type,
					size: file.size,
				},
			}));
		} catch (err) {
			console.error(`Required doc upload failed for docType ${docTypeId}:`, err);
		} finally {
			setRequiredDocUploadingId(null);
			e.target.value = '';
		}
	};

	const handleRemoveRequiredDoc = (docTypeId: number) => {
		setRequiredDocUploads((prev) => {
			const next = { ...prev };
			delete next[docTypeId];
			return next;
		});
	};

	// Build the unified section responses for a condition. Used by both the
	// full submit path and the per-card Save Draft action, so the shape stays
	// in lockstep across both writes to the same voucher row.
	const buildSectionResponses = useCallback(
		(condition: Condition): SectionResponse[] => {
			const condResponses = dataRequestResponses[condition.id] || {};
			const uploadedDocs = conditionUploadedDocs[condition.id] || {};
			const condOverrides = fieldOverrides[condition.id] || {};

			const buildFieldResponse = (field: DataRequestField): DataRequestResponse => {
				const base: DataRequestResponse = {
					fieldId: field.id,
					label: field.label,
					fieldType: field.fieldType,
					value: condResponses[field.id] || null,
					answeredAt: new Date().toISOString(),
				};

				// Document-type fields: attach uploaded document reference(s)
				if (field.fieldType === 'document') {
					const docs = uploadedDocs[field.id] || [];
					if (docs.length > 0) {
						// Backward compatible: always set single doc fields
						base.documentId = docs[0].id;
						base.documentName = docs[0].name;
						base.value = docs.map((d) => d.name).join(', ');
						// Multi-doc: set arrays when more than one
						if (docs.length > 1) {
							base.documentIds = docs.map((d) => d.id);
							base.documentNames = docs.map((d) => d.name);
						}
					}
				}

				// Inline exception note (when answer fails expectation rules)
				if (isUnexpectedAnswer(field, condResponses[field.id])) {
					const note = ((exceptionNotes[condition.id] || {})[field.id] || '').trim();
					if (note) {
						base.exceptionNote = note;
					}
				}

				// Override / skip reason. A real upload takes precedence, so only
				// record the override when the requirement wasn't otherwise met by a
				// document.
				const override = condOverrides[field.id];
				const hasDoc = field.fieldType === 'document' && (uploadedDocs[field.id] || []).length > 0;
				if (override?.overridden && !hasDoc) {
					base.overridden = true;
					base.overrideComment = override.comment;
				}

				return base;
			};

			// Helper: compute exception from fields that have expectation rules
			const computeException = (fields: DataRequestField[]) => {
				const expectationFields = fields.filter(
					(f) => f.fieldType === 'yesno' || f.expectedResponse != null
				);
				const hasException = expectationFields.some((f) =>
					isUnexpectedAnswer(f, condResponses[f.id])
				);
				const reasons = expectationFields
					.filter((f) => isUnexpectedAnswer(f, condResponses[f.id]))
					.map((f) => {
						const note = ((exceptionNotes[condition.id] || {})[f.id] || '').trim();
						return note ? `${f.label}: ${note}` : null;
					})
					.filter(Boolean);
				return {
					hasException,
					exceptionReason: hasException && reasons.length > 0 ? reasons.join('; ') : null,
				};
			};

			const sections = condition.verificationSections;
			if (!sections || sections.length === 0) return [];

			return sections.map((section) => {
				const sectionFields = section.fields || [];
				const exception = computeException(sectionFields);
				const isTemplateSection = section.source === 'TEMPLATE';
				return {
					sectionId: section.id,
					sectionName: section.name,
					// conditionId always points to the OWNING milestone so per-voucher
					// roll-ups stay aligned. Template provenance is carried via the
					// isTemplate/conditionName fields below and is sourced from the
					// section's `source` / `sourceConditionName` markers populated by
					// the backend composition.
					conditionId: condition.id,
					status: 'PENDING' as const,
					verifiedBy: null,
					verifiedAt: null,
					rejectedBy: null,
					rejectedAt: null,
					rejectionReason: null,
					hasException: exception.hasException,
					exceptionReason: exception.exceptionReason,
					fieldResponses: sectionFields.map(buildFieldResponse),
					...(isTemplateSection
						? {
								isTemplate: true as const,
								conditionName: section.sourceConditionName || undefined,
							}
						: {}),
				};
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dataRequestResponses, conditionUploadedDocs, fieldOverrides, exceptionNotes]
	);

	// Save Draft \u2014 PIU mirror of the supplier-side "Save Draft" action. Writes
	// in-progress section data to the per-condition GENERATED voucher without
	// promoting status or requiring the payment-side metadata that full submit
	// demands. PATCHes when a GENERATED voucher already exists for this
	// condition (typically seeded by the supplier portal); POSTs a minimal
	// GENERATED voucher otherwise.
	const handleSaveDraft = async (condition: Condition) => {
		if (!contract) return;
		setSavingDraftConditionId(condition.id);
		try {
			const sectionResponses = buildSectionResponses(condition).map((s) => ({
				...s,
				status: 'PENDING' as const,
			}));
			const existing = existingVouchersByCondition[condition.id];
			let result;
			if (existing) {
				result = await updateVoucher(existing.id, { sectionResponses });
			} else {
				const supplierEntity = contract.entities?.find((e) => e.type === 'SUPPLIER');
				const payload: VoucherPayload = {
					conditionId: condition.id,
					contractId: contract.id,
					activityId: contract.planningActivity,
					amount: 0,
					currency: contractCurrency,
					recipientType: 'VENDOR',
					recipientEntityId: supplierEntity?.id || null,
					status: 'GENERATED',
					sectionResponses,
				};
				result = await createVoucher(payload);
			}
			if (result.error) {
				setBanner({
					message: result.error.message || 'Failed to save draft.',
					severity: 'error',
				});
				return;
			}
			setBanner({ message: t('draftSavedSuccess'), severity: 'success' });
			await refetchVouchers();
		} catch (err) {
			console.error('Save draft error:', err);
			setBanner({ message: 'Failed to save draft. Please try again.', severity: 'error' });
		} finally {
			setSavingDraftConditionId(null);
		}
	};

	const handleSubmit = async () => {
		if (!contract) return;

		// The button is intentionally always clickable; when the form is
		// incomplete, reveal the blocking items inline instead of processing.
		if (!isFormValid) {
			setSubmitAttempted(true);
			return;
		}

		setIsSubmitting(true);

		try {
			// Get selected conditions for payment (all items are eligible, none are paid)
			const selectedConditions = conditionSelections.filter((cs) => cs.isChecked);

			// Separate payment conditions (contract milestones) from base conditions (work plan gates)
			// Legacy template clones (sourceCondition set) — pre-existing rows only;
			// new flow appends template sections inline to each milestone's voucher.
			const paymentConditionsToProcess = selectedConditions.filter(
				(cs) => cs.condition.conditionType === 'payment'
			);
			const baseConditionsToProcess = selectedConditions.filter(
				(cs) => cs.condition.conditionType === 'base'
			);

			// Build notes including override reason if applicable

			// Earmark + exchange-rate fields apply to BOTH direct-payment and
			// invoice-driven flows — they belong on every voucher, not just
			// the direct-payment classification block. Story #88.
			//
			// Exchange-rate STORAGE convention (must match VoucherPayment
			// reconciliation): each entry is `1 unit of entry.currency =
			// entry.exchangeRate units of the project REPORTING currency`. The FX
			// widget above is anchored on the source-account currency so it can
			// render "1 <reporting> = <rate> <account>" (reporting on the left),
			// which is the INVERSE of the stored convention. We therefore derive
			// the persisted rates here, keyed by each source-account currency and
			// valued in reporting units, via convertViaFx — otherwise a voucher
			// denominated in a non-reporting currency (e.g. PHP, reporting USD)
			// has no rate the reconciliation can match and shows "rate missing".
			const reportingCcyForFx =
				project?.currencies?.find((c) => c.type === 'REPORTING')?.currency || contractCurrency;
			const canonicalExchangeRates: ExchangeRateByDate[] | null = (() => {
				if (!reportingCcyForFx) return null;
				const dateForRate =
					exchangeRates.find((er) => er.currency === reportingCcyForFx && er.exchangeRate > 0)
						?.dateExchangeRate ||
					exchangeRates.find((er) => er.exchangeRate > 0)?.dateExchangeRate ||
					'';
				const ccys = new Set<string>();
				selectedFromAccounts.forEach((a) => a.currency && ccys.add(a.currency));
				if (contractCurrency) ccys.add(contractCurrency);
				const list: ExchangeRateByDate[] = [];
				ccys.forEach((c) => {
					if (c === reportingCcyForFx) return;
					// reporting units per 1 unit of c
					const rate = convertViaFx(1, c, reportingCcyForFx);
					if (rate != null && rate > 0) {
						list.push({ currency: c, exchangeRate: rate, dateExchangeRate: dateForRate });
					}
				});
				return list.length > 0 ? list : null;
			})();
			const earmarkAndFxFields = {
				exchangeRates: canonicalExchangeRates,
				// Single earmark goes directly on every voucher; multi-earmark
				// is overridden per-split inside the submit loop below.
				spendPlanId: selectedEarmarkPlanIds.length === 1 ? selectedEarmarkPlanIds[0] : null,
			};

			// Classification fields — every voucher carries the activity's
			// classification by default. The direct-payment picker (shown only
			// when the activity has no classification of its own) acts as an
			// explicit override when its values are non-null. Account routing,
			// loan attribution and free-text comments come from the picker too.
			//
			// Expense-type required documents that were skipped with a reason are
			// not part of the voucher's field responses (they attach as uploads),
			// so their reasons are folded into the persisted voucher comments to
			// keep them visible to reviewers on the voucher page.
			const requiredDocSkipNote = requiredDocTypes
				.filter((dt) => requiredDocSkips[dt.id]?.trim())
				.map((dt) => `${dt.name}: ${requiredDocSkips[dt.id].trim()}`)
				.join('; ');
			const combinedComments = [
				voucherComments.trim(),
				requiredDocSkipNote ? `Skipped required documents — ${requiredDocSkipNote}` : '',
			]
				.filter(Boolean)
				.join('\n');
			const classificationFields = {
				componentId: selectedComponentId ?? activityClassification.componentId,
				categoryId: selectedCategoryId ?? activityClassification.categoryId,
				expenseTypeId: selectedExpenseTypeId ?? activityClassification.expenseTypeId,
				fromAccountId: selectedFromAccountId,
				designatedAccountId: selectedDesignatedAccountId,
				// Legacy single-loan column the v1 mirror writer still reads;
				// populated from the first financing-source entry so single-source
				// vouchers round-trip into v1 without a mirror-writer change.
				loanId: financingSourcesLoanId,
				comments: combinedComments || null,
			};

			// Per-voucher split of the dialog-level financing-sources entries:
			// each voucher gets its proportional share of every entered loan row,
			// keyed by the voucher's amount as a fraction of the total typed into
			// the widget. When nothing has been entered but the source account
			// has exactly one loan, we auto-attribute the full voucher (amount +
			// tax) to that loan instead of leaving `financing_sources` null —
			// that's the production case today and it removes the per-voucher
			// data gap that the migration scripts have to back-fill.
			const splitFinancingSourcesForVoucher = (
				voucherAmount: number,
				voucherTax: number
			): { loanId: number; amount: number; tax: number }[] | null => {
				if (financingSourcesArray.length === 0) {
					if (soleLoanId == null) return null;
					return [{ loanId: soleLoanId, amount: voucherAmount, tax: voucherTax ?? 0 }];
				}
				if (financingSourcesTotal <= 0) return null;
				const share = (voucherAmount + (voucherTax ?? 0)) / financingSourcesTotal;
				return financingSourcesArray.map((s) => ({
					loanId: s.loanId,
					amount: Math.round(s.amount * share * 100) / 100,
					tax: Math.round(s.tax * share * 100) / 100,
				}));
			};

			// Update base conditions status to APPROVED (they are gates, not payments)
			// We create vouchers for them to track that they've been fulfilled as part of this payment
			const supplierEntity = contract.entities?.find((e) => e.type === 'SUPPLIER');

			for (const cs of baseConditionsToProcess) {
				// Create voucher to track the base condition was met for this contract's payment
				const voucherPayload: VoucherPayload = {
					conditionId: cs.condition.id,
					contractId: contract.id,
					activityId: contract.planningActivity,
					amount: cs.condition.paymentAmount || 0, // Base conditions may have 0 amount
					// Voucher currency follows the source account of the (single)
					// selected earmark; falls back to contract currency when no
					// earmark is selected. Story #88.
					currency: getSpendPlanCurrency(earmarkAndFxFields.spendPlanId),
					recipientType: 'VENDOR', // Database constraint: VENDOR or BENEFICIARY
					recipientEntityId: supplierEntity?.id || null,
					status: 'VERIFIED', // VERIFIED = "Awaiting Review" bucket on Payments page
					sectionResponses: buildSectionResponses(cs.condition),
					paymentRoute: paymentMethod,
					channel: paymentChannel as VoucherChannel,
					...classificationFields,
					...earmarkAndFxFields,
					taxWithheld: (() => {
						// Base conditions don't multi-split, so use the single-earmark key.
						const raw =
							taxWithheldBySplit[getTaxKey(cs.condition.id, earmarkAndFxFields.spendPlanId)];
						if (!raw) return null;
						const mode = taxModeByCondition[cs.condition.id] || 'amount';
						if (mode === 'percent') {
							const base = cs.condition.paymentAmount || 0;
							return (base * parseFloat(raw)) / 100;
						}
						return parseFloat(raw);
					})(),
				};

				const result = existingVouchersByCondition[cs.condition.id]
					? await updateVoucher(existingVouchersByCondition[cs.condition.id].id, voucherPayload)
					: await createVoucher(voucherPayload);
				if (result.error) {
					setBanner({
						message: result.error.message || 'Failed to create voucher.',
						severity: 'error',
					});
					return;
				}
				console.log('Base condition voucher result:', result);

				// Update condition status to APPROVED
				await updateCondition(cs.condition.id, { status: 'APPROVED' });
			}

			// Process payment conditions - these are the actual payments
			if (paymentConditionsToProcess.length === 0 && baseConditionsToProcess.length === 0) {
				// No conditions selected - this shouldn't happen with validation, but handle it
				console.warn('No conditions selected for payment');
			} else {
				// Multi-earmark: each earmark creates a separate voucher per condition
				const isMultiEarmark = selectedEarmarkPlanIds.length > 1;

				// Tracks supplier-owned GENERATED vouchers we've already
				// consumed by PATCH; subsequent per-earmark splits for the
				// same condition must POST a new voucher.
				const reusedVoucherIds = new Set<number>();

				// Create a voucher for each payment condition and update condition status
				for (const cs of paymentConditionsToProcess) {
					// Calculate the amount from condition. A percentage milestone
					// against a contract with no commitment in its own currency
					// resolves to 0 — the only sensible figure when there is no
					// base to multiply, and visible at the gross-amount row so
					// the user can correct the upstream data before submitting.
					const conditionAmount =
						cs.condition.paymentAmount ||
						(cs.condition.paymentPercentage && contractAmount != null
							? (contractAmount * cs.condition.paymentPercentage) / 100
							: 0);

					// Build the per-earmark splits (or single voucher if no multi-earmark)
					const earmarkSplits = isMultiEarmark
						? selectedEarmarkPlanIds.map((planId) => ({
								spendPlanId: planId,
								amount: earmarkAmounts[planId] || 0,
							}))
						: [{ spendPlanId: earmarkAndFxFields.spendPlanId, amount: conditionAmount }];

					for (const split of earmarkSplits) {
						if (isMultiEarmark && split.amount <= 0) continue;

						// The voucher is denominated in its source account's currency
						// (Story #88). For a single earmark the milestone gross is in the
						// CONTRACT currency, so convert it into the account currency via
						// the entered FX; for multi-earmark the user already typed the
						// per-earmark amount in the account currency. This is what keeps a
						// USD 200 milestone paid from a PHP account recorded as the correct
						// PHP figure rather than ₱200.
						const spCcy = getSpendPlanCurrency(split.spendPlanId);
						const amountInCcy = isMultiEarmark
							? split.amount
							: (convertViaFx(conditionAmount, contractCurrency, spCcy) ?? conditionAmount);

						// Tax in the voucher's own currency: percent mode multiplies the
						// account-currency amount; amount mode is typed in that currency.
						const taxRaw = taxWithheldBySplit[getTaxKey(cs.condition.id, split.spendPlanId)];
						let taxWithheld: number | null = null;
						if (taxRaw) {
							const mode = taxModeByCondition[cs.condition.id] || 'amount';
							taxWithheld =
								mode === 'percent' ? (amountInCcy * parseFloat(taxRaw)) / 100 : parseFloat(taxRaw);
						}

						const voucherPayload: VoucherPayload = {
							conditionId: cs.condition.id,
							contractId: contract.id,
							activityId: contract.planningActivity,
							amount: amountInCcy,
							// Per-split currency: each spend plan is tied to one source
							// account so multi-earmark payments naturally produce a voucher
							// per currency. Story #88.
							currency: spCcy,
							recipientType: 'VENDOR',
							recipientEntityId: supplierEntity?.id || null,
							status: 'VERIFIED',
							sectionResponses: buildSectionResponses(cs.condition),
							paymentRoute: paymentMethod,
							channel: paymentChannel as VoucherChannel,
							...classificationFields,
							...earmarkAndFxFields,
							spendPlanId: split.spendPlanId,
							taxWithheld,
							financingSources: splitFinancingSourcesForVoucher(amountInCcy, taxWithheld ?? 0),
						};

						// Reuse the supplier-owned GENERATED voucher (one per
						// condition) when present so the supplier's saved field
						// values / uploads aren't replaced by a parallel voucher
						// created here. Multi-earmark still creates additional
						// per-split vouchers — only the first split for this
						// condition reuses the existing one.
						const existingForCondition = existingVouchersByCondition[cs.condition.id];
						const shouldReuse =
							existingForCondition && !reusedVoucherIds.has(existingForCondition.id);
						const result = shouldReuse
							? await updateVoucher(existingForCondition.id, voucherPayload)
							: await createVoucher(voucherPayload);
						if (shouldReuse) reusedVoucherIds.add(existingForCondition.id);
						if (result.error) {
							setBanner({
								message: result.error.message || 'Failed to create voucher.',
								severity: 'error',
							});
							return;
						}
						console.log('Payment condition voucher result:', result);
					}

					// Don't update condition status yet — it stays VERIFIED until the voucher is redeemed (payment confirmed)
				}
			}

			// Refetch vouchers and conditions to update the UI
			await Promise.all([refetchVouchers(), refetchConditions()]);

			// Close dialog and notify parent
			onSuccess?.();
			onClose();
		} catch (error) {
			console.error('Error creating voucher:', error);
			setBanner({ message: 'Failed to prepare payment. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	// Render Helpers

	const renderContextHeader = () => (
		<Paper
			elevation={0}
			sx={{
				bgcolor: alpha(theme.palette.primary.main, 0.04),
				border: '1px solid',
				borderColor: alpha(theme.palette.primary.main, 0.15),
				borderRadius: 2,
				p: 2,
			}}>
			<Stack spacing={2}>
				{/* Row 1: Work Plan & Activity */}
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
					<Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
						<Box
							sx={{
								alignItems: 'center',
								bgcolor: alpha(theme.palette.primary.main, 0.1),
								borderRadius: 1,
								display: 'flex',
								height: 36,
								justifyContent: 'center',
								width: 36,
							}}>
							<IconWorkPlan sx={{ color: 'primary.main', fontSize: 20 }} />
						</Box>
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t('spendPlan.workPlan', 'Work Plan')}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{contract?.workPlanName}
							</Typography>
						</Box>
					</Stack>
					<Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
						<Box
							sx={{
								alignItems: 'center',
								bgcolor: alpha(theme.palette.primary.main, 0.1),
								borderRadius: 1,
								display: 'flex',
								height: 36,
								justifyContent: 'center',
								width: 36,
							}}>
							<IconActivity sx={{ color: 'primary.main', fontSize: 20 }} />
						</Box>
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t('spendPlan.activity', 'Activity')}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{contract?.activityName}
							</Typography>
						</Box>
					</Stack>
				</Stack>

				{/* Row 2: Contract & Supplier */}
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
					<Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
						<Box
							sx={{
								alignItems: 'center',
								bgcolor: alpha(theme.palette.info.main, 0.1),
								borderRadius: 1,
								display: 'flex',
								height: 36,
								justifyContent: 'center',
								width: 36,
							}}>
							<IconContract sx={{ color: 'info.main', fontSize: 20 }} />
						</Box>
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t('contract', 'Contract')}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{contract?.num || `#${contract?.id}`}
							</Typography>
						</Box>
					</Stack>
					<Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
						<Box
							sx={{
								alignItems: 'center',
								bgcolor: alpha(theme.palette.success.main, 0.1),
								borderRadius: 1,
								display: 'flex',
								height: 36,
								justifyContent: 'center',
								width: 36,
							}}>
							<IconPerson sx={{ color: 'success.main', fontSize: 20 }} />
						</Box>
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t('supplier', 'Supplier')}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{supplierName || '-'}
							</Typography>
						</Box>
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	);

	const renderConditionCard = (
		condition: Condition,
		isBase: boolean,
		isChecked: boolean,
		isPaid: boolean,
		isTemplate = false
	) => {
		const isVerified = condition.status === 'VERIFIED' || condition.status === 'APPROVED';
		// Check if condition has unfulfilled required data request fields (even if status is VERIFIED)
		const requiredFields = getAllFields(condition).filter((f) => f.required);
		const responses = dataRequestResponses[condition.id] || {};
		const docs = conditionUploadedDocs[condition.id] || {};
		const hasUnfulfilledReqs =
			requiredFields.length > 0 &&
			!requiredFields.every((f) => isFieldSatisfied(condition.id, f, responses, docs));
		const isUnmet = !isPaid && (!isVerified || hasUnfulfilledReqs);
		const canToggle = !isPaid && !isTemplate;

		// Get current uploaded docs for data request fields

		// Collapse logic: unchecked, non-paid conditions with sections are collapsed by default
		const hasSections =
			!isPaid &&
			condition.verificationSections &&
			condition.verificationSections.length > 0 &&
			condition.verificationSections.some((s) => (s.fields || []).length > 0);
		const isExpanded = isChecked || isPaid || expandedConditionIds.has(condition.id);
		const isCollapsed = !isExpanded && hasSections;

		// Existing GENERATED voucher (if any) carries in-flight draft data
		// shared between supplier and PIU. Surface a chip in the header so
		// reviewers can tell at a glance which conditions already have work
		// staged on them.
		const existingDraft = existingVouchersByCondition[condition.id];
		const draftStatusChip = (() => {
			if (!existingDraft || existingDraft.status !== 'GENERATED') return null;
			const sections = existingDraft.sectionResponses || [];
			if (sections.length === 0) return null;
			const allSubmitted = sections.every((s) => (s.status as string) === 'SUBMITTED');
			if (allSubmitted) return { label: t('submittedBySupplier'), color: 'success' as const };
			const anyPending = sections.some((s) => s.status === 'PENDING');
			if (anyPending) return { label: t('draftSaved'), color: 'info' as const };
			return null;
		})();

		// If the supplier has staged work on this milestone (draft chip present),
		// the right-side status is no longer "Not Started" — reflect that they
		// submitted (all sections SUBMITTED) or saved an in-progress draft.
		const supplierStagedLabel = draftStatusChip
			? draftStatusChip.label === t('submittedBySupplier')
				? 'Submitted'
				: 'In Progress'
			: null;

		// Save Draft visibility: only meaningful when the condition collects
		// data and isn't already past the draft stage on the voucher.
		const draftVoucherStatus = existingDraft?.status;
		const canSaveDraft =
			!isPaid &&
			!isTemplate &&
			hasSections &&
			(!draftVoucherStatus || draftVoucherStatus === 'GENERATED');
		const isSavingDraft = savingDraftConditionId === condition.id;

		return (
			<Paper
				key={condition.id}
				elevation={0}
				sx={{
					bgcolor: isPaid
						? alpha(theme.palette.success.main, 0.05)
						: isUnmet
							? alpha(theme.palette.grey[500], 0.08)
							: 'background.paper',
					border: '1px solid',
					borderColor: isPaid
						? alpha(theme.palette.success.main, 0.3)
						: isUnmet
							? alpha(theme.palette.grey[500], 0.3)
							: 'divider',
					borderRadius: 2,
					opacity: isPaid ? 0.85 : 1,
					overflow: 'hidden',
					transition: 'border-color 0.2s',
				}}>
				<Stack direction="row" alignItems="flex-start" sx={{ p: 2, pb: isCollapsed ? 0 : 2 }}>
					{/* Checkbox — not shown for legacy additional-requirement clones (always required) */}
					{!isTemplate ? (
						<FormControlLabel
							control={
								<Checkbox
									checked={isChecked}
									disabled={!canToggle}
									onChange={() => handleConditionToggle(condition.id)}
									sx={{ mt: -0.5 }}
								/>
							}
							label=""
							sx={{ m: 0, mr: 1 }}
						/>
					) : null}
					{/* Content */}
					<Box flex={1}>
						{/* Header Row */}
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{ mb: 0.25 }}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
									{condition.name}
								</Typography>
								{isPaid && (
									<Tooltip title={t('alreadyPaid', 'Already paid')}>
										<IconPaid sx={{ color: 'success.main', fontSize: 18 }} />
									</Tooltip>
								)}
								{!isPaid && isVerified && !hasUnfulfilledReqs && (
									<Tooltip title={t('eligible', 'Eligible for payment')}>
										<IconCheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
									</Tooltip>
								)}
								{!isPaid && isVerified && hasUnfulfilledReqs && (
									<Tooltip title={t('requirementsPending', 'Requirements not yet fulfilled')}>
										<IconWarning sx={{ color: 'warning.main', fontSize: 18 }} />
									</Tooltip>
								)}
								{draftStatusChip && (
									<Chip
										size="small"
										variant="outlined"
										label={draftStatusChip.label}
										color={draftStatusChip.color}
									/>
								)}
							</Stack>
							{/* Status label */}
							<Typography
								variant="caption"
								sx={{
									fontWeight: 600,
									color: isPaid
										? 'success.main'
										: !isPaid && isVerified && !hasUnfulfilledReqs
											? 'success.main'
											: !isPaid && isVerified && hasUnfulfilledReqs
												? 'warning.main'
												: condition.status === 'SUBMITTED'
													? 'info.main'
													: condition.status === 'REJECTED'
														? 'error.main'
														: supplierStagedLabel
															? 'info.main'
															: 'text.disabled',
									textTransform: 'uppercase',
									letterSpacing: '0.05em',
								}}>
								{isPaid
									? 'Disbursed'
									: !isPaid && isVerified && !hasUnfulfilledReqs
										? 'Eligible'
										: !isPaid && isVerified && hasUnfulfilledReqs
											? 'Action Needed'
											: condition.status === 'SUBMITTED'
												? 'Under Review'
												: condition.status === 'REJECTED'
													? 'Returned'
													: supplierStagedLabel || 'Not Started'}
							</Typography>
						</Stack>

						{/* Collapsible body — preview with gradient when collapsed */}
						<Box sx={{ position: 'relative' }}>
							<Box
								sx={{
									...(isCollapsed && {
										maxHeight: 80,
										overflow: 'hidden',
									}),
								}}>
								{/* Description */}
								{condition.description && (
									<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
										{condition.description}
									</Typography>
								)}

								{/* Invoice card(s) linked to this milestone */}
								{contract &&
									getInvoicesByContract(contract.id)
										.filter((inv) => inv.milestoneNumber === condition.id)
										.map((inv) => (
											<Box key={inv.id} sx={{ mb: 1.5 }}>
												<CardInvoiceDetails invoice={inv} />
											</Box>
										))}

								{/* Requirements Sections — grouped data entry fields */}
								{!isPaid &&
									condition.verificationSections &&
									condition.verificationSections.length > 0 &&
									condition.verificationSections.map((section, sIdx) => {
										const assignee = projectUsers.find((u) => u.id === section.assignedTo);
										const sectionFields = section.fields || [];
										if (sectionFields.length === 0) return null;
										const statusColor =
											section.status === 'VERIFIED'
												? 'success'
												: section.status === 'REJECTED'
													? 'error'
													: 'info';
										return (
											<Box
												key={section.id}
												sx={{
													border: '1px solid',
													borderColor: 'divider',
													borderRadius: 1,
													bgcolor: 'background.paper',
													overflow: 'hidden',
													mb: 1.5,
												}}>
												{/* Section header — matches the invoice card header style */}
												<Stack
													direction="row"
													spacing={0.75}
													alignItems="center"
													sx={{
														px: 1.5,
														py: 1,
														bgcolor: 'action.hover',
														borderBottom: '1px solid',
														borderColor: 'divider',
													}}>
													<IconGroup sx={{ fontSize: 20, color: 'primary.main' }} />
													<Typography sx={{ fontWeight: 700, color: 'text.primary', flex: 1 }}>
														{section.name || `Section ${sIdx + 1}`}
													</Typography>
													{section.source === 'TEMPLATE' && (
														<Chip
															label={
																section.sourceConditionName
																	? `Additional Requirement · ${section.sourceConditionName}`
																	: 'Additional Requirement'
															}
															size="small"
															variant="outlined"
															color="secondary"
														/>
													)}
													{assignee && (
														<Chip
															label={`${assignee.firstName} ${assignee.lastName}`}
															size="small"
															variant="outlined"
														/>
													)}
													<Chip
														label={section.status || 'PENDING'}
														size="small"
														color={statusColor}
														variant="outlined"
													/>
												</Stack>
												{/* Section fields — same rendering as flat fields */}
												<Box sx={{ p: 1.5 }}>
													<Stack spacing={2}>
														{sectionFields.map((field) => {
															const value = dataRequestResponses[condition.id]?.[field.id] || '';
															const isRequired = field.required;
															const isFilled = value.trim().length > 0;

															return (
																<Box key={field.id}>
																	<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
																		{field.label}
																		{isRequired && (
																			<Typography component="span" color="error" sx={{ ml: 0.5 }}>
																				*
																			</Typography>
																		)}
																	</Typography>

																	{/* Text field */}
																	{field.fieldType === 'text' && (
																		<TextField
																			fullWidth
																			placeholder={field.placeholder || 'Enter text...'}
																			size="small"
																			value={value}
																			onChange={(e) =>
																				handleDataRequestResponse(
																					condition.id,
																					field.id,
																					e.target.value
																				)
																			}
																			error={isChecked && isRequired && !isFilled}
																		/>
																	)}

																	{/* Number field */}
																	{field.fieldType === 'number' && (
																		<TextField
																			placeholder={field.placeholder || 'Enter number...'}
																			size="small"
																			sx={{ width: 200 }}
																			type="number"
																			value={value}
																			onChange={(e) =>
																				handleDataRequestResponse(
																					condition.id,
																					field.id,
																					e.target.value
																				)
																			}
																			error={isChecked && isRequired && !isFilled}
																			InputProps={
																				field.unit
																					? {
																							endAdornment: (
																								<Typography
																									color="text.secondary"
																									variant="body2"
																									sx={{ ml: 1 }}>
																									{field.unit}
																								</Typography>
																							),
																						}
																					: undefined
																			}
																		/>
																	)}

																	{/* Date field */}
																	{field.fieldType === 'date' && (
																		<TextField
																			size="small"
																			sx={{ width: 200 }}
																			type="date"
																			value={value}
																			onChange={(e) =>
																				handleDataRequestResponse(
																					condition.id,
																					field.id,
																					e.target.value
																				)
																			}
																			error={isChecked && isRequired && !isFilled}
																		/>
																	)}

																	{/* Select field */}
																	{field.fieldType === 'select' && (
																		<TextField
																			select
																			size="small"
																			sx={{ minWidth: 200 }}
																			value={value}
																			onChange={(e) =>
																				handleDataRequestResponse(
																					condition.id,
																					field.id,
																					e.target.value
																				)
																			}
																			error={isChecked && isRequired && !isFilled}>
																			<MenuItem value="">
																				<em>{field.placeholder || 'Select an option...'}</em>
																			</MenuItem>
																			{(field.options || []).map((opt, optIdx) => (
																				<MenuItem key={optIdx} value={opt}>
																					{opt}
																				</MenuItem>
																			))}
																		</TextField>
																	)}

																	{/* Inline exception for non-yesno fields with expectation rules */}
																	{field.fieldType !== 'yesno' &&
																		field.expectedResponse != null && (
																			<Collapse in={isUnexpectedAnswer(field, value)} timeout={200}>
																				<Box
																					sx={{
																						bgcolor: alpha(theme.palette.error.main, 0.04),
																						border: '1px solid',
																						borderColor: alpha(theme.palette.error.main, 0.2),
																						borderRadius: 1,
																						mt: 1,
																						p: 1.5,
																					}}>
																					<Stack
																						direction="row"
																						alignItems="center"
																						spacing={0.5}
																						sx={{ mb: 0.75 }}>
																						<IconWarning
																							sx={{ fontSize: 16, color: 'error.main' }}
																						/>
																						<Typography
																							variant="caption"
																							sx={{ fontWeight: 600, color: 'error.main' }}>
																							Exception —{' '}
																							{getExpectationFailureReason(field, value)}
																						</Typography>
																					</Stack>
																					<TextField
																						fullWidth
																						multiline
																						minRows={2}
																						maxRows={4}
																						size="small"
																						placeholder="Explain why this exception is acceptable (min 10 characters)..."
																						value={
																							(exceptionNotes[condition.id] || {})[field.id] || ''
																						}
																						onChange={(e) =>
																							handleExceptionNote(
																								condition.id,
																								field.id,
																								e.target.value
																							)
																						}
																						error={
																							isUnexpectedAnswer(field, value) &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length > 0 &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length < 10
																						}
																						helperText={
																							isUnexpectedAnswer(field, value) &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length > 0 &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length < 10
																								? 'Minimum 10 characters required'
																								: ''
																						}
																					/>
																				</Box>
																			</Collapse>
																		)}

																	{/* Yes/No field */}
																	{field.fieldType === 'yesno' && (
																		<>
																			<Stack direction="row" spacing={0.5}>
																				<Chip
																					label={t('yes', 'Yes')}
																					size="small"
																					color={value === 'yes' ? 'success' : 'default'}
																					variant={value === 'yes' ? 'filled' : 'outlined'}
																					onClick={() =>
																						handleDataRequestResponse(condition.id, field.id, 'yes')
																					}
																					sx={{
																						cursor: 'pointer',
																						height: 28,
																						fontSize: '1.25rem',
																						minWidth: 56,
																						'&:hover': {
																							bgcolor:
																								value !== 'yes'
																									? alpha(theme.palette.success.main, 0.1)
																									: undefined,
																						},
																					}}
																				/>
																				<Chip
																					label={t('no', 'No')}
																					size="small"
																					color={value === 'no' ? 'error' : 'default'}
																					variant={value === 'no' ? 'filled' : 'outlined'}
																					onClick={() =>
																						handleDataRequestResponse(condition.id, field.id, 'no')
																					}
																					sx={{
																						cursor: 'pointer',
																						height: 28,
																						fontSize: '1.25rem',
																						minWidth: 56,
																						'&:hover': {
																							bgcolor:
																								value !== 'no'
																									? alpha(theme.palette.error.main, 0.1)
																									: undefined,
																						},
																					}}
																				/>
																			</Stack>

																			{/* Inline exception explanation — appears when answer differs from expected */}
																			<Collapse in={isUnexpectedAnswer(field, value)} timeout={200}>
																				<Box
																					sx={{
																						bgcolor: alpha(theme.palette.error.main, 0.04),
																						border: '1px solid',
																						borderColor: alpha(theme.palette.error.main, 0.2),
																						borderRadius: 1,
																						mt: 1,
																						p: 1.5,
																					}}>
																					<Stack
																						direction="row"
																						alignItems="center"
																						spacing={0.5}
																						sx={{ mb: 0.75 }}>
																						<IconWarning
																							sx={{ fontSize: 16, color: 'error.main' }}
																						/>
																						<Typography
																							variant="caption"
																							sx={{ fontWeight: 600, color: 'error.main' }}>
																							Exception — please explain why
																						</Typography>
																					</Stack>
																					<TextField
																						fullWidth
																						multiline
																						minRows={2}
																						maxRows={4}
																						size="small"
																						placeholder="Explain why this exception is acceptable (min 10 characters)..."
																						value={
																							(exceptionNotes[condition.id] || {})[field.id] || ''
																						}
																						onChange={(e) =>
																							handleExceptionNote(
																								condition.id,
																								field.id,
																								e.target.value
																							)
																						}
																						error={
																							isUnexpectedAnswer(field, value) &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length > 0 &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length < 10
																						}
																						helperText={
																							isUnexpectedAnswer(field, value) &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length > 0 &&
																							(
																								(exceptionNotes[condition.id] || {})[field.id] || ''
																							).trim().length < 10
																								? 'Minimum 10 characters required'
																								: ''
																						}
																					/>
																				</Box>
																			</Collapse>
																		</>
																	)}

																	{/* Document upload field */}
																	{field.fieldType === 'document' &&
																		(() => {
																			const docs =
																				(conditionUploadedDocs[condition.id] || {})[field.id] || [];
																			const maxDocs = field.maxDocuments ?? 1;
																			const limitLabel =
																				maxDocs === 0
																					? `${docs.length}`
																					: `${docs.length} of ${maxDocs}`;
																			const atLimit = maxDocs > 0 && docs.length >= maxDocs;
																			const isUploading = uploadingField[condition.id] === field.id;
																			const uploadError = (uploadErrors[condition.id] || {})[
																				field.id
																			];
																			const skipOverride = (fieldOverrides[condition.id] || {})[
																				field.id
																			];
																			const isSkipped =
																				!!skipOverride?.overridden && docs.length === 0;
																			return (
																				<Box>
																					{/* Show uploaded documents */}
																					{docs.map((doc) => (
																						<Stack
																							key={doc.id}
																							direction="row"
																							alignItems="center"
																							spacing={1}
																							sx={{
																								bgcolor: alpha(theme.palette.success.main, 0.06),
																								border: '1px solid',
																								borderColor: alpha(theme.palette.success.main, 0.3),
																								borderRadius: 1,
																								px: 1.5,
																								py: 0.75,
																								mb: 0.5,
																							}}>
																							<IconCheckCircle
																								sx={{ fontSize: 16, color: 'success.main' }}
																							/>
																							<Typography
																								variant="body2"
																								sx={{
																									flex: 1,
																									fontWeight: 500,
																									...(onDocClick && {
																										cursor: 'pointer',
																										color: 'primary.main',
																										'&:hover': { textDecoration: 'underline' },
																									}),
																								}}
																								noWrap
																								onClick={
																									onDocClick
																										? () => onDocClick(doc.id, doc.name)
																										: undefined
																								}>
																								{doc.name}
																							</Typography>
																							<Tooltip title="Remove">
																								<Button
																									size="small"
																									color="error"
																									onClick={() =>
																										handleRemoveConditionDoc(
																											condition.id,
																											field.id,
																											doc.id
																										)
																									}
																									sx={{ minWidth: 'auto', p: 0.25 }}>
																									<IconClose sx={{ fontSize: 16 }} />
																								</Button>
																							</Tooltip>
																						</Stack>
																					))}
																					{/* Upload / Pick actions */}
																					<Stack
																						direction="row"
																						alignItems="center"
																						spacing={1.5}
																						sx={docs.length > 0 ? { mt: 0.5 } : {}}>
																						{!atLimit && (
																							<>
																								<Button
																									component="label"
																									disabled={isUploading}
																									size="small"
																									startIcon={
																										isUploading ? (
																											<CircularProgress size={16} />
																										) : (
																											<IconCloudUpload />
																										)
																									}
																									variant="outlined">
																									{isUploading ? 'Uploading...' : 'Upload File'}
																									<input
																										hidden
																										type="file"
																										onChange={(e) =>
																											handleConditionDocUpload(
																												condition.id,
																												field.id,
																												e
																											)
																										}
																									/>
																								</Button>
																								<Button
																									size="small"
																									variant="text"
																									onClick={() =>
																										openDocPicker(
																											condition.id,
																											field.id,
																											maxDocs,
																											docs
																										)
																									}>
																									Pick Existing
																								</Button>
																							</>
																						)}
																						<Chip
																							label={limitLabel}
																							size="small"
																							color={
																								docs.length > 0
																									? 'success'
																									: isChecked && isRequired
																										? 'warning'
																										: 'default'
																							}
																							variant="outlined"
																							sx={{ height: 20, fontSize: '1.25rem' }}
																						/>
																						{isChecked &&
																							isRequired &&
																							docs.length === 0 &&
																							!isSkipped && (
																								<Button
																									size="small"
																									variant="text"
																									color="warning"
																									onClick={() =>
																										openSkipDialog(
																											condition.id,
																											field.id,
																											field.label
																										)
																									}>
																									Skip
																								</Button>
																							)}
																					</Stack>
																					{isSkipped ? (
																						<Stack
																							direction="row"
																							alignItems="center"
																							spacing={1}
																							sx={{ mt: 0.5 }}>
																							<Typography
																								variant="caption"
																								sx={{ color: 'warning.main', fontWeight: 600 }}>
																								Skipped — {skipOverride?.comment}
																							</Typography>
																							<Button
																								size="small"
																								variant="text"
																								onClick={() => undoSkip(condition.id, field.id)}
																								sx={{ minWidth: 'auto', py: 0 }}>
																								Undo
																							</Button>
																						</Stack>
																					) : (
																						isChecked &&
																						isRequired &&
																						docs.length === 0 &&
																						!uploadError && (
																							<Typography
																								variant="caption"
																								color="error"
																								sx={{ display: 'block', mt: 0.5 }}>
																								Required — please upload or select a document
																							</Typography>
																						)
																					)}
																					{uploadError && (
																						<Alert
																							severity="error"
																							sx={{ mt: 0.5, py: 0, fontSize: '0.75rem' }}>
																							{uploadError}
																						</Alert>
																					)}
																				</Box>
																			);
																		})()}
																</Box>
															);
														})}
													</Stack>
												</Box>
											</Box>
										);
									})}

								{/* Payment & Tax Summary */}
								{(condition.paymentAmount || condition.paymentPercentage) &&
									(() => {
										const grossAmount =
											condition.paymentAmount ||
											(condition.paymentPercentage && contractAmount != null
												? (contractAmount * condition.paymentPercentage) / 100
												: 0);
										const taxMode = taxModeByCondition[condition.id] || 'amount';

										// Per-split tax: in multi-earmark mode iterate selected earmarks;
										// in single-earmark mode use the (sole) selected earmark or null.
										// Each split is denominated in its own account currency.
										const isMultiEarmarkUI = selectedEarmarkPlanIds.length > 1;
										const taxSplits = isMultiEarmarkUI
											? selectedEarmarkPlanIds.map((planId) => {
													const alloc = earmarkAllocations.find((e) => e.spendPlanId === planId);
													const splitAmount = earmarkAmounts[planId] || 0;
													const ccy = alloc
														? getEarmarkCurrency(alloc.accountId)
														: contractCurrency;
													return {
														spendPlanId: planId,
														spendPlanName: alloc?.spendPlanName || `Plan ${planId}`,
														amount: splitAmount,
														currency: ccy,
													};
												})
											: (() => {
													// Single earmark: the milestone gross is denominated in the
													// CONTRACT currency. Express it in the source account's
													// currency (the voucher currency) using the entered FX so the
													// number and its label always agree — this is what prevents a
													// USD 200 milestone from being mislabeled as ₱200. When the
													// rate isn't entered yet, fall back to the contract currency
													// (number + label together) rather than relabeling.
													const spendPlanId = selectedEarmarkPlanIds[0] ?? (null as number | null);
													const spCcy = getSpendPlanCurrency(spendPlanId);
													const convertedGross = convertViaFx(grossAmount, contractCurrency, spCcy);
													const resolved = convertedGross != null;
													return [
														{
															spendPlanId,
															spendPlanName: '',
															amount: resolved ? (convertedGross as number) : grossAmount,
															currency: resolved ? spCcy : contractCurrency,
														},
													];
												})();

										// Compute per-split net + per-currency totals.
										const splitTax = taxSplits.map((s) => {
											const raw = taxWithheldBySplit[getTaxKey(condition.id, s.spendPlanId)];
											const num = raw ? parseFloat(raw) : 0;
											const tax =
												num > 0 ? (taxMode === 'percent' ? (s.amount * num) / 100 : num) : 0;
											return { ...s, taxRaw: raw || '', taxAmount: tax, net: s.amount - tax };
										});
										const grossByCurrency: Record<string, number> = {};
										const taxByCurrency: Record<string, number> = {};
										const netByCurrency: Record<string, number> = {};
										for (const s of splitTax) {
											grossByCurrency[s.currency] = (grossByCurrency[s.currency] || 0) + s.amount;
											taxByCurrency[s.currency] = (taxByCurrency[s.currency] || 0) + s.taxAmount;
											netByCurrency[s.currency] = (netByCurrency[s.currency] || 0) + s.net;
										}
										const anyTaxEntered = splitTax.some((s) => s.taxAmount > 0);

										return (
											<Box
												sx={{
													bgcolor: alpha(theme.palette.grey[500], 0.04),
													borderRadius: 1,
													p: 1.5,
													mt: 1,
												}}>
												{/* Gross amount row(s) — one per currency */}
												{Object.entries(grossByCurrency).map(([ccy, amt]) => {
													// v1-style conversion column: show the reporting/contract
													// equivalent of this account-currency figure using the
													// entered FX (omitted when already in the contract currency).
													const reportingEquiv =
														ccy !== contractCurrency
															? convertViaFx(amt, ccy, contractCurrency)
															: null;
													return (
														<Stack
															key={`gross-${ccy}`}
															direction="row"
															justifyContent="space-between"
															alignItems="center">
															<Typography variant="body2" color="text.secondary">
																Gross amount
																{condition.paymentPercentage &&
																Object.keys(grossByCurrency).length === 1
																	? ` (${condition.paymentPercentage}%)`
																	: ''}
																{Object.keys(grossByCurrency).length > 1 ? ` (${ccy})` : ''}
															</Typography>
															<Stack alignItems="flex-end">
																<Typography variant="body2" sx={{ fontWeight: 600 }}>
																	{formatCurrency({ value: amt, currency: ccy })}
																</Typography>
																{reportingEquiv != null && (
																	<Typography variant="caption" color="text.secondary">
																		={' '}
																		{formatCurrency({
																			value: reportingEquiv,
																			currency: contractCurrency,
																		})}
																	</Typography>
																)}
															</Stack>
														</Stack>
													);
												})}

												{/* Tax mode toggle (single source of truth per condition) */}
												{isChecked && !isPaid && (
													<Box sx={{ mt: 1 }}>
														<Stack
															direction="row"
															alignItems="center"
															justifyContent="space-between"
															sx={{ mb: 1 }}>
															<Typography variant="caption" color="text.secondary">
																Tax withholding{isMultiEarmarkUI ? ' (per earmark)' : ''}
															</Typography>
															<Stack
																direction="row"
																sx={{
																	border: '1px solid',
																	borderColor: 'divider',
																	borderRadius: 1,
																	overflow: 'hidden',
																	flexShrink: 0,
																}}>
																<Button
																	size="small"
																	variant={taxMode === 'amount' ? 'contained' : 'text'}
																	onClick={() => {
																		setTaxModeByCondition((prev) => ({
																			...prev,
																			[condition.id]: 'amount',
																		}));
																		// Clear all tax inputs for this condition (currency
																		// semantics change between amount and percent).
																		setTaxWithheldBySplit((prev) => {
																			const next = { ...prev };
																			for (const s of taxSplits) {
																				delete next[getTaxKey(condition.id, s.spendPlanId)];
																			}
																			return next;
																		});
																	}}
																	sx={{
																		minWidth: 32,
																		px: 1,
																		py: 0.5,
																		borderRadius: 0,
																		fontSize: '0.75rem',
																	}}>
																	Amount
																</Button>
																<Button
																	size="small"
																	variant={taxMode === 'percent' ? 'contained' : 'text'}
																	onClick={() => {
																		setTaxModeByCondition((prev) => ({
																			...prev,
																			[condition.id]: 'percent',
																		}));
																		setTaxWithheldBySplit((prev) => {
																			const next = { ...prev };
																			for (const s of taxSplits) {
																				delete next[getTaxKey(condition.id, s.spendPlanId)];
																			}
																			return next;
																		});
																	}}
																	sx={{
																		minWidth: 32,
																		px: 1,
																		py: 0.5,
																		borderRadius: 0,
																		fontSize: '0.75rem',
																	}}>
																	%
																</Button>
															</Stack>
														</Stack>

														{/* One tax input per split. In single-earmark mode this is
																one row; in multi-earmark mode it's one per selected
																earmark, each in its own currency. */}
														<Stack spacing={1}>
															{taxSplits.map((s) => {
																const key = getTaxKey(condition.id, s.spendPlanId);
																return (
																	<TextField
																		key={key}
																		label={
																			isMultiEarmarkUI
																				? `Tax \u2014 ${s.spendPlanName}`
																				: 'Tax withholding'
																		}
																		type="number"
																		value={taxWithheldBySplit[key] || ''}
																		onChange={(e) =>
																			setTaxWithheldBySplit((prev) => ({
																				...prev,
																				[key]: e.target.value,
																			}))
																		}
																		placeholder={taxMode === 'percent' ? '20' : '0.00'}
																		InputProps={{
																			startAdornment:
																				taxMode === 'amount' ? (
																					<InputAdornment position="start">
																						{s.currency}
																					</InputAdornment>
																				) : undefined,
																			endAdornment:
																				taxMode === 'percent' ? (
																					<InputAdornment position="end">%</InputAdornment>
																				) : undefined,
																		}}
																		size="small"
																		fullWidth
																	/>
																);
															})}
														</Stack>
													</Box>
												)}

												{/* Tax & Net breakdown (per currency, shown when any tax entered) */}
												{anyTaxEntered && (
													<>
														{Object.entries(taxByCurrency).map(([ccy, amt]) =>
															amt > 0 ? (
																<Stack
																	key={`tax-${ccy}`}
																	direction="row"
																	justifyContent="space-between"
																	alignItems="center"
																	sx={{ mt: 0.75 }}>
																	<Typography variant="body2" color="text.secondary">
																		Tax withheld
																		{Object.keys(taxByCurrency).length > 1 ? ` (${ccy})` : ''}
																	</Typography>
																	<Typography
																		variant="body2"
																		sx={{ fontWeight: 500, color: 'error.main' }}>
																		{'\u2212'} {formatCurrency({ value: amt, currency: ccy })}
																	</Typography>
																</Stack>
															) : null
														)}
														<Divider sx={{ my: 0.5 }} />
														{Object.entries(netByCurrency).map(([ccy, amt]) => (
															<Stack
																key={`net-${ccy}`}
																direction="row"
																justifyContent="space-between"
																alignItems="center">
																<Typography variant="body2" sx={{ fontWeight: 600 }}>
																	Net disbursement
																	{Object.keys(netByCurrency).length > 1 ? ` (${ccy})` : ''}
																</Typography>
																<Typography
																	variant="body2"
																	sx={{
																		fontWeight: 700,
																		color: amt >= 0 ? 'success.main' : 'error.main',
																	}}>
																	{formatCurrency({ value: amt, currency: ccy })}
																</Typography>
															</Stack>
														))}
													</>
												)}
											</Box>
										);
									})()}
							</Box>
							{/* Gradient fade overlay when collapsed */}
							{isCollapsed && (
								<Box
									sx={{
										position: 'absolute',
										bottom: 0,
										left: 0,
										right: 0,
										height: 60,
										background: `linear-gradient(to bottom, transparent, ${
											isUnmet
												? theme.palette.mode === 'dark'
													? 'rgba(30,30,30,1)'
													: 'rgba(245,245,245,1)'
												: theme.palette.background.paper
										})`,
										pointerEvents: 'none',
									}}
								/>
							)}
						</Box>
						{/* View more / View less toggle */}
						{hasSections && !isChecked && !isPaid && (
							<Button
								size="small"
								onClick={() =>
									setExpandedConditionIds((prev) => {
										const next = new Set(prev);
										if (next.has(condition.id)) {
											next.delete(condition.id);
										} else {
											next.add(condition.id);
										}
										return next;
									})
								}
								endIcon={isExpanded ? <IconExpandLess /> : <IconExpandMore />}
								sx={{
									textTransform: 'none',
									color: 'text.secondary',
									fontWeight: 500,
									fontSize: '1.2rem',
									mt: 0.5,
									mb: isCollapsed ? 1 : 0,
									alignSelf: 'flex-start',
								}}>
								{isExpanded ? 'View less' : 'View more'}
							</Button>
						)}
						{/* Save Draft footer \u2014 writes in-progress field data to the
							shared GENERATED voucher without requiring payment metadata. */}
						{canSaveDraft && (
							<Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
								<Button
									variant="outlined"
									size="small"
									disabled={isSavingDraft}
									onClick={() => handleSaveDraft(condition)}
									startIcon={isSavingDraft ? <CircularProgress size={14} /> : <IconSave />}>
									{t('saveDraft')}
								</Button>
							</Stack>
						)}
					</Box>
				</Stack>
			</Paper>
		);
	};

	const renderConditionsStep = () => (
		<Stack spacing={3}>
			{/* Context Header */}
			{renderContextHeader()}

			{Object.keys(existingVouchersByCondition).length > 0 && (
				<Alert severity="info" icon={<IconInfo />}>
					{`Supplier data on file for ${Object.keys(existingVouchersByCondition).length} milestone${Object.keys(existingVouchersByCondition).length === 1 ? '' : 's'}. Their entries and uploads have been loaded below — review, fill any remaining gaps, and submit to advance the payment.`}
				</Alert>
			)}

			{/* ── Funding & Payment ── (moved above conditions for Story #88 Group A: account+earmark before conditions) */}
			<Paper
				elevation={0}
				sx={{
					border: '1px solid',
					borderColor:
						fundingSource === 'spendPlan' && earmarksLoaded && earmarkAllocations.length === 0
							? 'warning.main'
							: 'divider',
					borderRadius: 2,
					overflow: 'hidden',
				}}>
				{/* Section header */}
				<Box
					sx={{
						bgcolor:
							fundingSource === 'spendPlan' && earmarksLoaded && earmarkAllocations.length === 0
								? alpha(theme.palette.warning.main, 0.06)
								: alpha(theme.palette.primary.main, 0.04),
						px: 2.5,
						py: 1.5,
						borderBottom: '1px solid',
						borderColor: 'divider',
					}}>
					<Stack direction="row" spacing={1} alignItems="center">
						<IconPaid
							sx={{
								color:
									fundingSource === 'spendPlan' && earmarksLoaded && earmarkAllocations.length === 0
										? 'warning.main'
										: 'primary.main',
								fontSize: 20,
							}}
						/>
						<Typography variant="subtitle2" fontWeight={700}>
							Funding &amp; Payment
						</Typography>
					</Stack>
				</Box>

				<Stack spacing={0} divider={<Divider />}>
					{/* ── Funding source toggle ────────────────────────── */}
					<Box sx={{ px: 2.5, py: 2 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							fontWeight={600}
							sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
							Funding Source
						</Typography>
						<ToggleButtonGroup
							exclusive
							size="small"
							value={fundingSource}
							onChange={(_, val: 'spendPlan' | 'account' | null) => {
								if (!val) return;
								setFundingSource(val);
								if (val === 'account') {
									// Direct funding: drop earmark selections so the manual
									// account picker drives the payment.
									setSelectedEarmarkPlanIds([]);
									setEarmarkAmounts({});
								} else if (earmarkAllocations.length === 1) {
									// Back to the spend-plan path: re-select the lone earmark
									// and its account.
									setSelectedEarmarkPlanIds([earmarkAllocations[0].spendPlanId]);
									setSelectedFromAccountId(earmarkAllocations[0].accountId);
								}
							}}
							sx={{ flexWrap: 'wrap' }}>
							<ToggleButton
								value="spendPlan"
								disabled={earmarksLoaded && earmarkAllocations.length === 0}>
								Spend Plan Earmark
							</ToggleButton>
							<ToggleButton value="account">Directly from Account</ToggleButton>
						</ToggleButtonGroup>
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
							{fundingSource === 'spendPlan'
								? 'Funds are drawn from an approved spend-plan earmark.'
								: 'Pay directly from an account with available balance — no spend plan required.'}
						</Typography>
					</Box>

					{/* ── Earmark sub-section ──────────────────────────── */}
					{fundingSource === 'spendPlan' && earmarksLoaded && earmarkAllocations.length === 0 && (
						<Box sx={{ px: 2.5, py: 2 }}>
							<Alert severity="warning" variant="outlined" sx={{ border: 0, p: 0 }}>
								No approved spend plan earmarks found for this activity. Approve a spend plan in{' '}
								<strong>Spend Planning</strong>, or switch to <strong>Directly from Account</strong>{' '}
								to pay from an account's available balance.
							</Alert>
						</Box>
					)}

					{fundingSource === 'spendPlan' &&
						earmarkAllocations.length > 0 &&
						(() => {
							const totalEarmarkRemaining = earmarkAllocations.reduce((s, a) => s + a.remaining, 0);
							const isInsufficient =
								selectedPayoutTotal > 0 && selectedPayoutTotal > totalEarmarkRemaining;
							return (
								<Box sx={{ px: 2.5, py: 2 }}>
									<Typography
										variant="caption"
										color="text.secondary"
										fontWeight={600}
										sx={{
											textTransform: 'uppercase',
											letterSpacing: 0.5,
											mb: 1.5,
											display: 'block',
										}}>
										Spend Plan Earmark
									</Typography>
									{earmarkAllocations.length === 1 ? (
										<>
											<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
												{earmarkAllocations[0].spendPlanName}
											</Typography>
											<Stack
												direction="row"
												spacing={3}
												sx={{ mb: selectedPayoutTotal > 0 ? 1.5 : 0 }}>
												<Box>
													<Typography variant="caption" color="text.secondary">
														Earmarked
													</Typography>
													<Typography variant="body2" fontWeight={600}>
														{formatCurrency({
															value: earmarkAllocations[0].amountPlanned,
															currency: getEarmarkCurrency(earmarkAllocations[0].accountId),
														})}
													</Typography>
												</Box>
												{earmarkAllocations[0].amountVouchered > 0 && (
													<Box>
														<Typography variant="caption" color="text.secondary">
															Vouchered
														</Typography>
														<Typography variant="body2" fontWeight={600}>
															{formatCurrency({
																value: earmarkAllocations[0].amountVouchered,
																currency: getEarmarkCurrency(earmarkAllocations[0].accountId),
															})}
														</Typography>
													</Box>
												)}
												<Box>
													<Typography variant="caption" color="text.secondary">
														Remaining
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														color={isInsufficient ? 'error.main' : 'success.main'}>
														{formatCurrency({
															value: earmarkAllocations[0].remaining,
															currency: getEarmarkCurrency(earmarkAllocations[0].accountId),
														})}
													</Typography>
												</Box>
											</Stack>
											{selectedPayoutTotal > 0 && (
												<Box
													sx={{
														bgcolor: isInsufficient
															? alpha(theme.palette.error.main, 0.06)
															: alpha(theme.palette.success.main, 0.06),
														borderRadius: 1,
														p: 1,
													}}>
													<Stack direction="row" justifyContent="space-between" alignItems="center">
														<Typography
															variant="body2"
															color={isInsufficient ? 'error.main' : 'text.secondary'}>
															Selected payout
														</Typography>
														<Typography
															variant="body2"
															fontWeight={700}
															color={isInsufficient ? 'error.main' : 'text.primary'}>
															{formatCurrency({
																value: selectedPayoutTotal,
																currency: contractCurrency,
															})}
														</Typography>
													</Stack>
													{payoutLocalEquivalent(selectedPayoutTotal) && (
														<Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.25 }}>
															<Typography variant="caption" color="text.secondary">
																{payoutLocalEquivalent(selectedPayoutTotal)}
															</Typography>
														</Stack>
													)}
													{isInsufficient && (
														<Typography
															variant="caption"
															color="error.main"
															sx={{ mt: 0.5, display: 'block' }}>
															Payout exceeds earmark by{' '}
															{formatCurrency({
																value: selectedPayoutTotal - totalEarmarkRemaining,
																currency: contractCurrency,
															})}
														</Typography>
													)}
												</Box>
											)}
										</>
									) : (
										<Stack spacing={1}>
											{earmarkAllocations.map((alloc) => {
												const isSelected = selectedEarmarkPlanIds.includes(alloc.spendPlanId);
												return (
													<Paper
														key={alloc.spendPlanId}
														elevation={0}
														sx={{
															border: '1px solid',
															borderColor: isSelected ? 'primary.main' : 'divider',
															borderRadius: 1,
															p: 1.5,
															bgcolor: isSelected
																? alpha(theme.palette.primary.main, 0.04)
																: 'transparent',
														}}>
														<FormControlLabel
															control={
																<Checkbox
																	checked={isSelected}
																	onChange={(e) => {
																		if (e.target.checked) {
																			setSelectedEarmarkPlanIds((prev) => [
																				...prev,
																				alloc.spendPlanId,
																			]);
																			if (selectedEarmarkPlanIds.length === 0) {
																				setSelectedFromAccountId(alloc.accountId);
																			}
																		} else {
																			setSelectedEarmarkPlanIds((prev) =>
																				prev.filter((id) => id !== alloc.spendPlanId)
																			);
																			setEarmarkAmounts((prev) => {
																				const next = { ...prev };
																				delete next[alloc.spendPlanId];
																				return next;
																			});
																		}
																	}}
																	size="small"
																/>
															}
															label={
																<Box>
																	<Typography variant="body2" fontWeight={600}>
																		{alloc.spendPlanName}
																	</Typography>
																	<Typography variant="caption" color="text.secondary">
																		{formatCurrency({
																			value: alloc.remaining,
																			currency: getEarmarkCurrency(alloc.accountId),
																		})}{' '}
																		remaining
																		{' · '}Earmarked:{' '}
																		{formatCurrency({
																			value: alloc.amountPlanned,
																			currency: getEarmarkCurrency(alloc.accountId),
																		})}
																	</Typography>
																</Box>
															}
														/>
														{isSelected && selectedEarmarkPlanIds.length > 1 && (
															<TextField
																size="small"
																label="Amount from this earmark"
																type="number"
																value={earmarkAmounts[alloc.spendPlanId] ?? ''}
																onChange={(e) => {
																	const val = parseFloat(e.target.value) || 0;
																	const capped = Math.min(val, alloc.remaining);
																	setEarmarkAmounts((prev) => ({
																		...prev,
																		[alloc.spendPlanId]: capped,
																	}));
																}}
																InputProps={{
																	endAdornment: (
																		<InputAdornment position="end">
																			max{' '}
																			{formatCurrency({
																				value: alloc.remaining,
																				currency: getEarmarkCurrency(alloc.accountId),
																			})}
																		</InputAdornment>
																	),
																}}
																fullWidth
																sx={{ mt: 1 }}
															/>
														)}
													</Paper>
												);
											})}
											{selectedPayoutTotal > 0 && (
												<Box
													sx={{
														bgcolor: isInsufficient
															? alpha(theme.palette.error.main, 0.06)
															: alpha(theme.palette.success.main, 0.06),
														borderRadius: 1,
														p: 1,
													}}>
													<Stack direction="row" justifyContent="space-between" alignItems="center">
														<Typography
															variant="body2"
															color={isInsufficient ? 'error.main' : 'text.secondary'}>
															Selected payout
														</Typography>
														<Typography
															variant="body2"
															fontWeight={700}
															color={isInsufficient ? 'error.main' : 'text.primary'}>
															{formatCurrency({
																value: selectedPayoutTotal,
																currency: contractCurrency,
															})}
														</Typography>
													</Stack>
													{payoutLocalEquivalent(selectedPayoutTotal) && (
														<Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.25 }}>
															<Typography variant="caption" color="text.secondary">
																{payoutLocalEquivalent(selectedPayoutTotal)}
															</Typography>
														</Stack>
													)}
													{isInsufficient && (
														<Typography
															variant="caption"
															color="error.main"
															sx={{ mt: 0.5, display: 'block' }}>
															Payout exceeds earmark by{' '}
															{formatCurrency({
																value: selectedPayoutTotal - totalEarmarkRemaining,
																currency: contractCurrency,
															})}
														</Typography>
													)}
													{/* Cross-currency reconciliation — sum of per-earmark
													    allocations converted to the contract currency via
													    the exchange rates entered below. Story #88. */}
													{(() => {
														const allocated = earmarkAllocatedInContract.total;
														const unresolved = earmarkAllocatedInContract.unresolved;
														const diff = allocated - selectedPayoutTotal;
														const mismatch = !unresolved && Math.abs(diff) > 0.005;
														return (
															<>
																<Stack
																	direction="row"
																	justifyContent="space-between"
																	alignItems="center"
																	sx={{ mt: 0.5 }}>
																	<Typography
																		variant="caption"
																		color={mismatch ? 'error.main' : 'text.secondary'}>
																		Allocated from earmarks
																	</Typography>
																	<Typography
																		variant="caption"
																		fontWeight={600}
																		color={mismatch ? 'error.main' : 'text.primary'}>
																		{unresolved
																			? '— (enter exchange rates)'
																			: `≈ ${formatCurrency({
																					value: allocated,
																					currency: contractCurrency,
																				})}`}
																	</Typography>
																</Stack>
																{mismatch && (
																	<Typography
																		variant="caption"
																		color="error.main"
																		sx={{ mt: 0.25, display: 'block' }}>
																		{diff > 0
																			? `Allocations exceed payout by ${formatCurrency({ value: diff, currency: contractCurrency })}`
																			: `Allocations short by ${formatCurrency({ value: -diff, currency: contractCurrency })}`}
																	</Typography>
																)}
															</>
														);
													})()}
												</Box>
											)}
										</Stack>
									)}
								</Box>
							);
						})()}

					{/* ── Account & Routing ─────────────────────────────
					    Mirrors the v1 transaction form exactly: a single
					    <Grid container> holding Sending Account → Designated
					    Account → Exchange Rates → Financing Sources, using the
					    same <Select> field, <ListItemTextAccount> option labels
					    and i18n keys as FormAddTransaction. */}
					<Box sx={{ px: 2.5, py: 2 }}>
						<Grid container spacing={2}>
							{fundingSource === 'spendPlan' &&
							earmarkAllocations.length > 0 &&
							selectedFromAccounts.length > 0 ? (
								// Earmark-driven: source accounts are derived from the
								// selected spend-plan allocations and shown read-only.
								<Grid item xs={12}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: 'block', mb: 1 }}>
										{selectedFromAccounts.length === 1
											? t('sendingAccount')
											: `${t('sendingAccount')} (${selectedFromAccounts.length})`}
									</Typography>
									<Stack spacing={1}>
										{selectedFromAccounts.map((a) => (
											<Box
												key={a.id}
												sx={{
													border: '1px solid',
													borderColor: 'divider',
													borderRadius: 1,
													px: 1.5,
													py: 1,
													bgcolor: 'background.default',
												}}>
												<ListItemTextAccount account={a} />
											</Box>
										))}
									</Stack>
								</Grid>
							) : (
								<Grid item xs={12} lg={12}>
									<Select
										id="form-prepare-payment-account"
										label={t('sendingAccount')}
										name="fromAccount"
										onChange={(e: SelectChangeEvent<unknown>) => {
											const v = e.target.value as string;
											setSelectedFromAccountId(v ? Number(v) : null);
										}}
										options={directAccounts
											.filter((a) => a.status !== 'DEACTIVATED')
											.map((a) => ({
												disabled: a.status === 'DEACTIVATED',
												label: <ListItemTextAccount account={a} />,
												secondary: a.institution,
												value: String(a.id),
											}))}
										required={true}
										value={selectedFromAccountId != null ? String(selectedFromAccountId) : ''}
									/>
								</Grid>
							)}
							{designatedAccounts.length > 0 && (
								<Grid item xs={12} lg={12}>
									<Select
										helperText={t('formAddTransaction.designatedAccount.helper')}
										id="form-prepare-payment-designated-account"
										label={t('designatedAccount')}
										name="designatedAccount"
										onChange={(e: SelectChangeEvent<unknown>) => {
											const v = e.target.value as string;
											setSelectedDesignatedAccountId(v ? Number(v) : null);
										}}
										options={designatedAccounts.map((a) => ({
											disabled: a.status === 'DEACTIVATED',
											label: <ListItemTextAccount account={a} />,
											secondary: a.institution,
											value: String(a.id),
										}))}
										required={true}
										value={
											selectedDesignatedAccountId != null ? String(selectedDesignatedAccountId) : ''
										}
									/>
								</Grid>
							)}
							{fxBaseCurrency && exchangeRates.length > 0 && (
								<Grid item xs={12}>
									<ExchangeRates
										currencyBase={fxBaseCurrency}
										exchangeRates={exchangeRates}
										onChange={(idx, name, value) =>
											handleChangeExchangeRatesForBase(
												fxBaseCurrency,
												exchangeRates[idx].currency,
												name,
												value
											)
										}
										required={true}
									/>
								</Grid>
							)}
							{availableLoans.length > 0 && selectedAccountData && (
								<Grid item xs={12}>
									{availableLoans.length === 1 ? (
										// One-loan case: there's no allocation choice to make,
										// so we render a read-only chip instead of a single-row
										// Amounts widget. The voucher will be attributed to this
										// loan automatically at submit time (see
										// `splitFinancingSourcesForVoucher`).
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography
												variant="caption"
												color="text.secondary"
												fontWeight={600}
												sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
												{t('financingSources')}
											</Typography>
											<Chip
												size="small"
												label={availableLoans[0].name || `Loan #${availableLoans[0].id}`}
												title={`Voucher amount + tax will be attributed to ${
													availableLoans[0].name || `loan #${availableLoans[0].id}`
												}.`}
											/>
										</Stack>
									) : (
										<form id="form-prepare-payment-amounts" onSubmit={(e) => e.preventDefault()}>
											<Amounts
												balances={selectedAccountData.balances}
												currency={fxBaseCurrency || selectedAccountData.currency || ''}
												exchangeRates={exchangeRates}
												formId="form-prepare-payment-amounts"
												hasCurrency={false}
												hasTax={true}
												loans={availableLoans}
												title={t('financingSources')}
												onChange={(
													e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
													_total: number,
													loan: Loan
												) => {
													const isTax = e.target.name.includes('tax');
													const v = Number(e.target.value) || 0;
													setFinancingSourceAmounts((prev) => {
														const next = [...prev];
														const found = next.find((a) => a.loan === loan.id);
														if (found) {
															if (isTax) found.tax = v;
															else found.amount = v;
														} else {
															next.push({
																loan: loan.id,
																amount: isTax ? 0 : v,
																tax: isTax ? v : 0,
															});
														}
														return next;
													});
												}}
											/>
											{financingSourcesArray.length > 0 &&
												selectedEarmarkPlanIds.length <= 1 &&
												!isFinancingSourcesValid && (
													<Typography
														variant="caption"
														color="error.main"
														sx={{ display: 'block', mt: 1 }}>
														Financing-source total must match the payment total (
														{financingSourcesExpectedTotal.toLocaleString(undefined, {
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														})}{' '}
														{fxBaseCurrency || ''} expected).
													</Typography>
												)}
										</form>
									)}
								</Grid>
							)}
						</Grid>
					</Box>

					{/* ── Payment Route ──────────────────────────────── */}
					<Box sx={{ px: 2.5, py: 2 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							fontWeight={600}
							sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
							Payment Route
						</Typography>
						<TextField
							select
							fullWidth
							value={paymentMethod}
							onChange={(e) => {
								const route = e.target.value as 'DA_REIMBURSEMENT' | 'DIRECT_PAYMENT';
								setPaymentMethod(route);
								if (route === 'DIRECT_PAYMENT') {
									setPaymentChannel('BANK');
								}
							}}
							size="small">
							<MenuItem value="DA_REIMBURSEMENT">DA / Reimbursement (In-Country)</MenuItem>
							<MenuItem value="DIRECT_PAYMENT">Direct Payment (Cross-Border)</MenuItem>
						</TextField>
						{paymentMethod === 'DIRECT_PAYMENT' && (
							<Alert severity="info" icon={<IconInfo />} sx={{ mt: 1.5 }}>
								Direct Payment will route through Client Connection (CC) and iLAP for cross-border
								payment processing. The transaction data will be pre-populated.
							</Alert>
						)}
					</Box>
				</Stack>
			</Paper>

			{/* Progress Summary */}
			{totalCount > 0 && (
				<Paper
					elevation={0}
					sx={{
						bgcolor: alpha(theme.palette.primary.main, 0.04),
						border: '1px solid',
						borderColor: alpha(theme.palette.primary.main, 0.15),
						borderRadius: 2,
						p: 2,
					}}>
					<Stack direction="row" spacing={3} flexWrap="wrap">
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t('spendPlan.preparePayment.selected', 'Selected')}
							</Typography>
							<Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
								{selectedCount}
							</Typography>
						</Box>
						<Divider orientation="vertical" flexItem />
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t('spendPlan.preparePayment.eligible', 'Eligible')}
							</Typography>
							<Typography variant="h6" sx={{ fontWeight: 600 }}>
								{totalCount}
							</Typography>
						</Box>
					</Stack>
				</Paper>
			)}

			{/* Conditions List */}
			{conditionSelections.length === 0 ? (
				<Alert severity="info">
					{t(
						'spendPlan.preparePayment.noConditions',
						'No conditions are defined for this contract.'
					)}
				</Alert>
			) : (
				<Stack spacing={3}>
					{/* Base Conditions Section (behind feature flag) */}
					{featureFlags.PREREQUISITES &&
						conditionSelections.filter((cs) => cs.isBase).length > 0 && (
							<Box>
								<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
									<IconGate sx={{ color: 'primary.main' }} fontSize="small" />
									<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
										{t('spendPlan.preparePayment.baseConditions', 'Base Conditions')}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										({t('spendPlan.preparePayment.fromWorkPlan', 'from Work Plan')})
									</Typography>
								</Stack>
								<Stack spacing={2}>
									{conditionSelections
										.filter((cs) => cs.isBase)
										.map(({ condition, isBase, isChecked, isPaid }) =>
											renderConditionCard(condition, isBase, isChecked, isPaid)
										)}
								</Stack>
							</Box>
						)}

					{/* Payment Conditions Section */}
					{conditionSelections.filter((cs) => !cs.isBase && !cs.isTemplate).length > 0 && (
						<Box>
							<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
								<IconContract sx={{ color: 'primary.main' }} fontSize="small" />
								<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
									{t('spendPlan.preparePayment.paymentConditions', 'Payment Conditions')}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									({t('spendPlan.preparePayment.contractMilestones', 'Contract Milestones')})
								</Typography>
							</Stack>
							<Stack spacing={2}>
								{conditionSelections
									.filter((cs) => !cs.isBase && !cs.isTemplate)
									.map(({ condition, isBase, isChecked, isPaid }) =>
										renderConditionCard(condition, isBase, isChecked, isPaid)
									)}
							</Stack>
						</Box>
					)}

					{/* Additional Requirements Section — only shown when a milestone is selected */}
					{conditionSelections.filter((cs) => cs.isTemplate).length > 0 &&
						conditionSelections.some((cs) => !cs.isBase && !cs.isTemplate && cs.isChecked) && (
							<Box>
								<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
									<IconGate sx={{ color: '#8b5cf6' }} fontSize="small" />
									<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
										Additional Requirements
									</Typography>
									<Typography variant="body2" color="text.secondary">
										(based on activity classification — required)
									</Typography>
								</Stack>
								<Stack spacing={2}>
									{conditionSelections
										.filter((cs) => cs.isTemplate)
										.map(({ condition, isBase, isChecked, isPaid }) =>
											renderConditionCard(condition, isBase, isChecked, isPaid, true)
										)}
								</Stack>
							</Box>
						)}
				</Stack>
			)}

			{/* Payment Method & Notes — only shown when a milestone is selected */}
			{milestoneSelectedCount > 0 && (
				<>
					{/* Classification Fields — direct payment path only (Story #43) */}
					{isDirectPayment && (
						<>
							<Divider />
							<Box>
								<Typography variant="subtitle2" fontWeight={600} gutterBottom>
									Payment Classification
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Direct contract payment — select the financial classification for this voucher.
								</Typography>
								<Stack spacing={2}>
									<TextField
										select
										fullWidth
										label="Component"
										value={selectedComponentId ?? ''}
										onChange={(e) =>
											setSelectedComponentId(e.target.value ? Number(e.target.value) : null)
										}
										size="small">
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{directComponents.map((c) => (
											<MenuItem key={c.id} value={c.id}>
												{c.name}
											</MenuItem>
										))}
									</TextField>
									<TextField
										select
										fullWidth
										label="Category"
										value={selectedCategoryId ?? ''}
										onChange={(e) =>
											setSelectedCategoryId(e.target.value ? Number(e.target.value) : null)
										}
										size="small">
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{directCategories.map((c) => (
											<MenuItem key={c.id} value={c.id}>
												{c.name}
											</MenuItem>
										))}
									</TextField>
									<TextField
										select
										fullWidth
										label="Expense Type"
										value={selectedExpenseTypeId ?? ''}
										onChange={(e) => {
											const val = e.target.value ? Number(e.target.value) : null;
											setSelectedExpenseTypeId(val);
											// Clear required doc uploads when expense type changes
											setRequiredDocUploads({});
										}}
										size="small">
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{directExpenseTypes.map((et) => (
											<MenuItem key={et.id} value={et.id}>
												{et.name}
											</MenuItem>
										))}
									</TextField>
								</Stack>
							</Box>
						</>
					)}

					{/* Required Documents Section (from expense type config in Settings) */}
					{requiredDocTypes.length > 0 && (
						<Paper
							elevation={0}
							sx={{
								border: '1px solid',
								borderColor: allRequiredDocsUploaded
									? 'divider'
									: alpha(theme.palette.warning.main, 0.5),
								borderRadius: 2,
								overflow: 'hidden',
							}}>
							<Box
								sx={{
									bgcolor: alpha(theme.palette.warning.main, 0.04),
									px: 2.5,
									py: 1.5,
									borderBottom: '1px solid',
									borderColor: 'divider',
								}}>
								<Stack direction="row" alignItems="center" spacing={1}>
									<IconAttachFile sx={{ color: 'warning.main', fontSize: 20 }} />
									<Typography variant="subtitle2" fontWeight={700}>
										Required Documents
									</Typography>
									<Typography variant="caption" color="text.secondary">
										(based on expense type)
									</Typography>
								</Stack>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: 'block', mt: 0.5 }}>
									To obtain 100% documentation traceability, upload every document listed below.
									Each document is optional — you may skip it with a reason, which lowers this
									payment's traceability score.
								</Typography>
							</Box>
							<Box sx={{ px: 2.5, py: 2 }}>
								{!allRequiredDocsUploaded && (
									<Alert severity="warning" variant="outlined" sx={{ mb: 2, border: 0, p: 0 }}>
										Upload or skip each required document to continue.
									</Alert>
								)}
								<Stack spacing={1.5}>
									{requiredDocTypes.map((dt) => {
										const uploaded = requiredDocUploads[dt.id];
										const skipReasonForDoc = requiredDocSkips[dt.id];
										const isUploading = requiredDocUploadingId === dt.id;
										return (
											<Paper
												key={dt.id}
												elevation={0}
												sx={{
													border: '1px solid',
													borderColor: uploaded
														? 'success.light'
														: alpha(theme.palette.warning.main, 0.4),
													borderRadius: 1.5,
													p: 1.5,
												}}>
												<Stack direction="row" alignItems="center" justifyContent="space-between">
													<Stack direction="row" alignItems="center" spacing={1}>
														{uploaded ? (
															<IconCheckCircle fontSize="small" sx={{ color: 'success.main' }} />
														) : (
															<IconWarning fontSize="small" sx={{ color: 'warning.main' }} />
														)}
														<Typography variant="body2" fontWeight={500}>
															{dt.name}
														</Typography>
														<Chip
															label="Required"
															size="small"
															color="warning"
															variant="outlined"
														/>
													</Stack>
													<Stack direction="row" alignItems="center" spacing={1}>
														{uploaded && (
															<Chip
																label={uploaded.name}
																size="small"
																onDelete={() => handleRemoveRequiredDoc(dt.id)}
																color="success"
																variant="outlined"
															/>
														)}
														{!uploaded && skipReasonForDoc && (
															<Tooltip title={skipReasonForDoc}>
																<Chip
																	label="Skipped"
																	size="small"
																	onDelete={() => undoRequiredDocSkip(dt.id)}
																	color="warning"
																	variant="outlined"
																/>
															</Tooltip>
														)}
														{!uploaded && !skipReasonForDoc && (
															<>
																<Button
																	component="label"
																	size="small"
																	variant="outlined"
																	startIcon={
																		isUploading ? (
																			<CircularProgress size={14} />
																		) : (
																			<IconCloudUpload />
																		)
																	}
																	disabled={isUploading}>
																	{isUploading ? 'Uploading...' : 'Upload'}
																	<input
																		type="file"
																		hidden
																		onChange={(e) => handleRequiredDocUpload(dt.id, e)}
																	/>
																</Button>
																<Button
																	size="small"
																	variant="text"
																	color="warning"
																	disabled={isUploading}
																	onClick={() => openRequiredDocSkipDialog(dt.id, dt.name)}>
																	Skip
																</Button>
															</>
														)}
													</Stack>
												</Stack>
											</Paper>
										);
									})}
								</Stack>
							</Box>
						</Paper>
					)}
				</>
			)}

			{/* ── Notes (moved to bottom for Story #88 Group A follow-up) ── */}
			<Paper
				elevation={0}
				sx={{
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 2,
					p: 2.5,
				}}>
				<Typography
					variant="caption"
					color="text.secondary"
					fontWeight={600}
					sx={{
						textTransform: 'uppercase',
						letterSpacing: 0.5,
						mb: 1.5,
						display: 'block',
					}}>
					Notes
				</Typography>
				<TextField
					fullWidth
					multiline
					onChange={(e) => setNotes(e.target.value)}
					placeholder={
						notesRequired
							? 'Provide a reason for the override (min 10 characters)...'
							: 'Optional notes about this payment...'
					}
					required={notesRequired}
					rows={2}
					value={notes}
					size="small"
					error={notesRequired && notes.trim().length > 0 && notes.trim().length < 10}
					helperText={
						notesRequired && notes.trim().length > 0 && notes.trim().length < 10
							? 'Minimum 10 characters required'
							: notesRequired
								? 'Required — explain why the payment should proceed despite overrides'
								: ''
					}
				/>
			</Paper>
		</Stack>
	);

	// Render

	if (!contract) return null;

	const titleNode = (
		<Stack alignItems="center" direction="row" justifyContent="space-between">
			<Stack direction="row" alignItems="center" spacing={1}>
				<IconSend color="primary" />
				<Typography variant="h6">
					{t('spendPlan.preparePayment.title', 'Prepare Contract for Payment')}
				</Typography>
			</Stack>
			{variant === 'dialog' && (
				<Button color="inherit" onClick={onClose} size="small">
					<IconClose />
				</Button>
			)}
		</Stack>
	);

	const bodyNode = isSubmitting ? (
		<BlockchainProcessing status="LOADING" />
	) : (
		<>
			{renderConditionsStep()}
			{submitAttempted && validationIssues.length > 0 && (
				<Alert severity="error" sx={{ mt: 2 }}>
					<Typography fontWeight={600} gutterBottom>
						{t(
							'spendPlan.preparePayment.incomplete',
							'Please resolve the following before creating the voucher:'
						)}
					</Typography>
					<ul style={{ margin: 0, paddingLeft: 20 }}>
						{validationIssues.map((issue) => (
							<li key={issue}>{issue}</li>
						))}
					</ul>
				</Alert>
			)}
		</>
	);

	const cancelLabel = variant === 'page' ? t('back', 'Back') : t('cancel', 'Cancel');

	const actionButtons = isSubmitting ? null : (
		<>
			<Button onClick={onClose} variant="outlined">
				{cancelLabel}
			</Button>

			<Button
				disabled={isSubmitting}
				onClick={handleSubmit}
				startIcon={isSubmitting ? <CircularProgress size={16} /> : <IconSend />}
				variant="contained">
				{isSubmitting
					? t('submitting', 'Submitting...')
					: t('spendPlan.preparePayment.submit', 'Create Voucher')}
			</Button>
		</>
	);

	const documentPicker = (
		<DialogDocumentPicker
			isOpen={pickerOpen}
			onClose={() => setPickerOpen(false)}
			onConfirm={handlePickerConfirm}
			projectId={project?.id}
			contractId={contract?.id}
			maxSelections={pickerMaxDocs}
			currentSelections={(conditionUploadedDocs[pickerConditionId]?.[pickerFieldId] || []).map(
				(d: UploadedDocument) => d.id
			)}
			title="Select Documents"
			uploadSourceType="condition"
			uploadSourceId={pickerConditionId}
		/>
	);

	// In the page variant the form is embedded directly in the page, so the
	// on-chain submit animation is surfaced as a modal overlay rather than
	// swapped inline into the page body (which reflows the layout and looks
	// broken). The dialog variant already shows it inside its own Dialog.
	const submittingDialogNode = (
		<Dialog open={isSubmitting} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
			<DialogContent sx={{ py: 4 }}>
				<BlockchainProcessing status="LOADING" />
			</DialogContent>
		</Dialog>
	);

	const skipDialogNode = (
		<Dialog open={!!skipDialog} onClose={() => setSkipDialog(null)} fullWidth maxWidth="xs">
			<DialogTitle sx={{ pb: 1 }}>Skip required document</DialogTitle>
			<DialogContent>
				<Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
					Traceability score will be impacted. Skipping a required document lowers this payment's
					documentation traceability — provide a reason for bypassing the requirement.
				</Alert>
				{skipDialog && (
					<Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
						{skipDialog.label}
					</Typography>
				)}
				<TextField
					autoFocus
					fullWidth
					multiline
					minRows={2}
					maxRows={4}
					size="small"
					label="Reason"
					required
					placeholder="Explain why this document is being skipped..."
					value={skipReason}
					onChange={(e) => setSkipReason(e.target.value)}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setSkipDialog(null)}>Cancel</Button>
				<Button
					variant="contained"
					color="warning"
					disabled={!skipReason.trim()}
					onClick={confirmSkip}>
					Skip Document
				</Button>
			</DialogActions>
		</Dialog>
	);

	if (variant === 'page') {
		return (
			<>
				<Box
					sx={{
						bgcolor: 'background.paper',
						border: 1,
						borderColor: 'divider',
						borderRadius: 1,
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
					}}>
					<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>{titleNode}</Box>
					<Box sx={{ p: 2 }}>{renderConditionsStep()}</Box>
					<Box
						sx={{
							p: 2,
							borderTop: 1,
							borderColor: 'divider',
							display: 'flex',
							justifyContent: 'flex-end',
							gap: 1,
							position: 'sticky',
							bottom: 0,
							bgcolor: 'background.paper',
							zIndex: 1,
						}}>
						{actionButtons}
					</Box>
				</Box>
				{submittingDialogNode}
				{documentPicker}
				{skipDialogNode}
			</>
		);
	}

	return (
		<>
			<Dialog fullWidth maxWidth="md" onClose={onClose} open={isOpen}>
				<DialogTitle sx={{ pb: 0 }}>{titleNode}</DialogTitle>

				<DialogContent sx={{ pt: 2 }}>{bodyNode}</DialogContent>

				<DialogActions>{actionButtons}</DialogActions>
			</Dialog>

			{documentPicker}
			{skipDialogNode}
		</>
	);
};
